import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  ArrowLeft,
  User,
  MapPin,
  Package,
  CreditCard,
  CheckCircle,
  Truck,
  BookOpen,
  Plus,
  Trash2,
  AlertTriangle,
  ChevronDown,
  ShieldCheck,
  X,
  Printer,
  FileText,
  Receipt,
  Phone,
  Mail,
  Navigation,
  Info,
  XCircle,
  ArrowRight,
  Wallet
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { CarrierLogo } from './ui/carrier-logo';
import { FlagIcon } from './ui/flag-icon';
import { tatToText, computeQuoteHighlights, badgesForIndex, carrierDisplayName } from '../lib/quoteBadges';
import api, { bookingsAPI, quotesAPI } from '../services/api';
import LoginModal from './auth/LoginModal';
import RegisterModal from './auth/RegisterModal';

// Prohibited Items List
const prohibitedItems = [
  "Explosives and Fireworks",
  "Flammable Liquids and Gases",
  "Toxic and Infectious Substances",
  "Radioactive Materials",
  "Corrosive Materials",
  "Weapons and Ammunition",
  "Illegal Drugs and Narcotics",
  "Counterfeit Goods",
  "Live Animals",
  "Human Remains",
  "Perishable Food Items (without proper packaging)",
  "Lithium Batteries (loose/uninstalled)",
  "Aerosols and Compressed Gases",
  "Magnetized Materials",
  "Cash and Negotiable Instruments"
];


// Smoothly expanding section (grid-rows trick animates to auto height).
const CollapsibleSection = ({ title, subtitle, summary, icon, open, onToggle, children }) => (
  <div className={`rounded-lg border transition-colors ${open ? 'border-orange-200 bg-white' : 'border-slate-200 bg-white'}`}>
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 rounded-lg transition-colors"
    >
      {icon ? <span className="shrink-0 text-orange-500">{icon}</span> : null}
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-slate-900">{title}</span>
        {!open && (summary || subtitle) ? (
          <span className="block text-sm text-slate-500 truncate mt-0.5">{summary || subtitle}</span>
        ) : null}
      </span>
      <ChevronDown
        className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
      />
    </button>
    <div
      className={`grid transition-all duration-300 ease-in-out ${
        open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
      }`}
    >
      <div className="overflow-hidden">
        <div className="px-4 pb-5 pt-1 border-t border-slate-100">{children}</div>
      </div>
    </div>
  </div>
);

// Chargeable weight, using the same rule the backend prices on
// (backend/utils/weight_utils.py): round the actual and the volumetric weight
// each up to the next 0.5 kg below 10 kg and the next whole kg above it, then
// charge the greater of the two. Kept in step deliberately - when the figure
// shown differs from the figure priced, the customer is quoted one parcel and
// billed for another.
const roundChargeable = (w) => {
  if (!w || w <= 0) return 0;
  return w <= 10 ? Math.ceil(w * 2) / 2 : Math.ceil(w);
};

const boxWeights = (box) => {
  const actual = parseFloat(box?.weight) || 0;
  const l = parseFloat(box?.length) || 0;
  const w = parseFloat(box?.width) || 0;
  const h = parseFloat(box?.height) || 0;
  const volumetric = l && w && h ? (l * w * h) / 5000 : 0;
  return {
    actual,
    volumetric,
    chargeable: Math.max(roundChargeable(actual), roundChargeable(volumetric)),
  };
};

const BookingFlow = ({ selectedQuote, quoteId, onBack, onComplete, onQuoteUpdate }) => {
  // Must match the FIRST id in stepOrder below (Shipment Boxes), not 1.
  // Step ids are stable but the visiting order changed, so starting at 1
  // dropped the customer straight into Sender Details and skipped boxes,
  // contents and instructions.
  const [currentStep, setCurrentStep] = useState(3);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [kycStatus, setKycStatus] = useState(null);
  const [showKycModal, setShowKycModal] = useState(false);
  const [bookingData, setBookingData] = useState({
    // Sender information
    senderName: '',
    senderPhone: '',
    senderEmail: '',
    senderCompany: '',
    senderAddressLine1: '',
    senderAddressLine2: '',
    senderCity: '',
    senderState: '',
    senderPostalCode: '',
    senderLandmark: '',
    senderCountry: 'India',

    // Recipient information
    recipientName: '',
    recipientPhone: '',
    recipientEmail: '',
    recipientCompany: '',
    recipientAddressLine1: '',
    recipientAddressLine2: '',
    recipientCity: '',
    recipientState: '',
    recipientPostalCode: '',
    recipientLandmark: '',
    recipientCountry: 'India',

    // Package information
    contentsDescription: '',
    packageQuantity: 1,
    hsnCode: '',
    notes: '',
    fragile: false,
    dangerousGoods: false,

    // Multiple contents (no weight field)
    contentItems: [
      { id: 1, description: '', quantity: 1, value: '', hsnCode: '' }
    ],

    // Package boxes with weight and dimensions
    packageBoxes: [
      { id: 1, weight: '', length: '', width: '', height: '' }
    ]
  });

  const [loading, setLoading] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [showProhibitedItems, setShowProhibitedItems] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [paymentError, setPaymentError] = useState(null);
  const [razorpayOrderData, setRazorpayOrderData] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [showVolumetricInfo, setShowVolumetricInfo] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [refreshInProgress, setRefreshInProgress] = useState(false);
  const [lastRefreshKey, setLastRefreshKey] = useState('');
  const [priceUpdateStatus, setPriceUpdateStatus] = useState('idle'); // 'idle', 'updating', 'updated'
  const [updateTimeout, setUpdateTimeout] = useState(null); // Store timeout reference
  const [alternativeQuotes, setAlternativeQuotes] = useState([]); // Store alternative carrier quotes
  // Full carrier list from the last refresh. alternativeQuotes excludes whatever
  // was selected at refresh time, so it can't be used to render the choices.
  const [freshQuotes, setFreshQuotes] = useState([]);
  const [showAlternatives, setShowAlternatives] = useState(false); // Show/hide alternatives
  const [recommendedAlternative, setRecommendedAlternative] = useState(null); // Best alternative
  
  // Wallet payment state
  const [walletBalance, setWalletBalance] = useState(null);
  // Discount state
  const [userDiscount, setUserDiscount] = useState(null); // { percentage, note }

  // Door pickup (charged by the pickup PIN code) or drop-off at our office
  // (free). The charge comes from the rate platform; the server prices it
  // again when the booking is made, so this is only what the customer sees.
  const [pickupMethod, setPickupMethod] = useState('pickup');
  // idle (no full PIN yet) | loading | ok | unavailable (no pickup there) | error
  const [pickupQuote, setPickupQuote] = useState({ status: 'idle' });
  const [pickupRetry, setPickupRetry] = useState(0);
  const pickupSeq = useRef(0);

  // Auto-invalidate carrier rates state
  const [ratesValid, setRatesValid] = useState(true); // Track if current rates are valid
  const [lastPackageSnapshot, setLastPackageSnapshot] = useState(null); // Snapshot of package details when rates were fetched
  const [showRateInvalidWarning, setShowRateInvalidWarning] = useState(false); // Show warning when trying to proceed with invalid rates
  const { toast } = useToast();
  const { user } = useAuth();
  // Guests fill in every detail first; sign-in is only required at payment.
  const [openPackageSection, setOpenPackageSection] = useState('dimensions');
  const [quoteOpen, setQuoteOpen] = useState(true);
  // Stays true from the moment rates go stale until the customer has re-picked
  // a carrier and moved on, so the step can't vanish mid-decision.
  const [requotePending, setRequotePending] = useState(false);
  // Identifies this booking on the server from before payment, so a failure
  // afterwards is recoverable rather than lost with the tab.
  const [attemptId] = useState(() => `att_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const [bookingFailure, setBookingFailure] = useState(null);
  const draftRef = useRef(null);
  // A stored quote made at payment time, for a rate that was never stored.
  const pricedQuoteRef = useRef(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  // Function to create a snapshot of package details for comparison
  const createPackageSnapshot = () => {
    return {
      packageBoxes: bookingData.packageBoxes.map(box => ({
        weight: box.weight,
        length: box.length,
        width: box.width,
        height: box.height
      })),
      packageCount: bookingData.packageBoxes.length
    };
  };

  // Function to check if package details have changed
  const hasPackageDetailsChanged = () => {
    if (!lastPackageSnapshot) return false;

    const currentSnapshot = createPackageSnapshot();

    // Check if package count changed
    if (currentSnapshot.packageCount !== lastPackageSnapshot.packageCount) {
      return true;
    }

    // Check if any package box details changed
    for (let i = 0; i < currentSnapshot.packageBoxes.length; i++) {
      const current = currentSnapshot.packageBoxes[i];
      const last = lastPackageSnapshot.packageBoxes[i];

      if (!last ||
        current.weight !== last.weight ||
        current.length !== last.length ||
        current.width !== last.width ||
        current.height !== last.height) {
        return true;
      }
    }

    return false;
  };

  // Function to invalidate rates when package details change
  const invalidateRates = () => {
    console.log('📦 Package details changed - invalidating carrier rates');
    setRatesValid(false);
    setRequotePending(true);
    setShowAlternatives(false);
    setAlternativeQuotes([]);
    setFreshQuotes([]);
    setRecommendedAlternative(null);
    setPriceUpdateStatus('idle');
    setShowRateInvalidWarning(false);
  };

  // Function to validate rates (called when Find Best Rates is clicked)
  const validateRates = () => {
    console.log('✅ Rates validated - package details snapshot updated');
    setRatesValid(true);
    setLastPackageSnapshot(createPackageSnapshot());
    setShowRateInvalidWarning(false);
  };

  // Function to get carrier logo
  const getCarrierLogo = (carrierName) => {
    if (!carrierName) return null;

    const carrierKey = carrierName.toLowerCase().replace(/\s+/g, '');
    const logoMap = {
      'indiapost': '/assets/images/carriers/indiapost-v2.svg',
      'sendmyparcel': '/assets/images/carriers/sendmyparcel.png',
      'xfas': '/assets/images/xfas-logo.png',
      'arame': '/assets/images/carriers/arame.png',
      'bluedart': '/assets/images/carriers/bluedart.png',
      'dhl': '/assets/images/carriers/dhl.png',
      'dtdc': '/assets/images/carriers/dtdc.png',
      'fedex': '/assets/images/carriers/fedex.png',
      'ups': '/assets/images/carriers/ups.png'
    };

    // Try exact match first
    if (logoMap[carrierKey]) {
      return logoMap[carrierKey];
    }

    // Try partial matches for common variations
    for (const [key, logo] of Object.entries(logoMap)) {
      if (carrierKey.includes(key) || key.includes(carrierKey)) {
        return logo;
      }
    }

    return null;
  };

  // Memoized pricing calculation - SIMPLIFIED to use actual selectedQuote data
  // Bring each new step into view. Critically, payment success advances to the
  // confirmation step (5) via a direct setCurrentStep(5) that does NOT scroll —
  // so without this, paying from the bottom of step 4 leaves the user scrolled
  // past the "Booking Confirmed" screen and it looks like nothing happened.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Surface a failed/cancelled payment as an inline error and scroll it into view.
  useEffect(() => {
    if (!paymentError) return;
    const el = document.querySelector('[data-payment-section]');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [paymentError]);

  // Changing the payment method clears any previous payment error.
  useEffect(() => {
    setPaymentError(null);
  }, [selectedPaymentMethod]);

  const pricingInfo = useMemo(() => {
    console.log('🧮 Using selectedQuote data directly for pricing:', {
      carrier: selectedQuote?.carrier_name,
      totalCost: selectedQuote?.total_cost,
      weight: selectedQuote?.weight,
      volumetricWeight: selectedQuote?.volumetric_weight,
      chargeableWeight: selectedQuote?.chargeable_weight
    });

    // Use the selectedQuote data directly - no local calculations
    const baseCost = selectedQuote?.total_cost || 0;
    const discountPct = userDiscount?.percentage || 0;
    const discountAmt = discountPct > 0 ? Math.round(baseCost * discountPct / 100 * 100) / 100 : 0;
    const shippingCost = Math.max(0, baseCost - discountAmt);
    const chargeableWeight = parseFloat(selectedQuote?.chargeable_weight) || Math.max(
      parseFloat(selectedQuote?.weight) || 0,
      parseFloat(selectedQuote?.volumetric_weight) || 0
    );
    // Door pickup: the PIN code's rate per kg on the chargeable weight, never
    // less than the minimum (5 kg), with no discount on it - the same
    // arithmetic the server uses. Its price is shown whichever option is
    // chosen; only a chosen pickup is added to the total. Drop-off is free.
    const pickupOffered = pickupQuote.status === 'ok';
    const pickupWeight = pickupOffered ? Math.max(chargeableWeight, pickupQuote.minimumKg) : 0;
    const pickupPrice = pickupOffered ? Math.round(pickupQuote.rate * pickupWeight * 100) / 100 : 0;
    const pickupCharge = pickupMethod === 'pickup' ? pickupPrice : 0;
    const finalCost = Math.round((shippingCost + pickupCharge) * 100) / 100;

    return {
      actualWeight: parseFloat(selectedQuote?.weight) || 0,
      volumetricWeight: parseFloat(selectedQuote?.volumetric_weight) || 0,
      chargeableWeight,
      baseCost,
      discountPercentage: discountPct,
      discountAmount: discountAmt,
      shippingCost,
      pickupWeight,
      pickupPrice,
      pickupCharge,
      totalCost: finalCost,
      isUpdated: false
    };
  }, [selectedQuote, userDiscount, pickupMethod, pickupQuote]);

  // Look up the pickup charge as soon as the sender's PIN code is complete.
  // A slow early answer never overwrites a later one.
  useEffect(() => {
    const pin = String(bookingData.senderPostalCode || '').replace(/\D/g, '');
    if (pin.length !== 6) {
      pickupSeq.current += 1;
      setPickupQuote({ status: 'idle' });
      return undefined;
    }
    const seq = ++pickupSeq.current;
    setPickupQuote({ status: 'loading', pincode: pin });
    const timer = setTimeout(async () => {
      try {
        const info = await quotesAPI.getPickupCharge(pin);
        if (seq !== pickupSeq.current) return;
        setPickupQuote(info?.serviceable
          ? { status: 'ok', pincode: pin, rate: Number(info.rate_per_kg) || 0,
              minimumKg: Number(info.minimum_kg) || 5, district: info.district }
          : { status: 'unavailable', pincode: pin });
      } catch (err) {
        if (seq === pickupSeq.current) setPickupQuote({ status: 'error', pincode: pin });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [bookingData.senderPostalCode, pickupRetry]);

  const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
  const pickupPriceLabel = {
    idle: 'Enter PIN code',
    loading: 'Checking…',
    ok: inr(pricingInfo.pickupPrice),
    unavailable: 'Not available',
    error: '—',
  }[pickupQuote.status];
  const pickupNote = pickupQuote.status === 'ok'
    ? `${pricingInfo.pickupWeight} kg × ₹${pickupQuote.rate}/kg (minimum ${pickupQuote.minimumKg} kg). `
      + `We collect it from ${pickupQuote.district || 'your address'}.`
    : `Charged per kg by the pickup PIN code, minimum 5 kg.`;

  // The server refuses wallet payment for a disabled or frozen wallet, so the
  // option is only offered when it can work. KYC is not needed to pay with it.
  const walletUsable = Boolean(
    walletBalance && walletBalance.balance > 0 &&
    walletBalance.is_active !== false && !walletBalance.is_frozen
  );

  useEffect(() => {
    if (selectedPaymentMethod === 'wallet' && !walletUsable) setSelectedPaymentMethod(null);
  }, [selectedPaymentMethod, walletUsable]);

  // A draft is priced when it is created. If the quote or the discount
  // changes, the next payment needs a new draft at the new price.
  useEffect(() => {
    draftRef.current = null;
    pricedQuoteRef.current = null;
  }, [selectedQuote, quoteId, userDiscount]);

  // Weights derived from what the customer has actually entered in the boxes
  // step. pricingInfo describes the quote, which was priced before those boxes
  // existed, so the two disagree the moment weight or dimensions change - and
  // it is these figures, not the quote's, that describe the parcel being sent.
  const enteredWeights = useMemo(() => {
    return (bookingData.packageBoxes || []).reduce(
      (acc, b) => {
        const { actual, volumetric, chargeable } = boxWeights(b);
        acc.actual += actual;
        acc.volumetric += volumetric;
        acc.chargeable += chargeable;
        return acc;
      },
      { actual: 0, volumetric: 0, chargeable: 0 }
    );
  }, [bookingData.packageBoxes]);

  // A quote priced on a different parcel is the wrong price. The threshold is a
  // full rounding step so that rounding alone can never block a booking - only
  // a real change in the parcel does.
  const quotedChargeable = pricingInfo.chargeableWeight || 0;
  const weightMismatch =
    enteredWeights.chargeable > 0 && Math.abs(enteredWeights.chargeable - quotedChargeable) > 0.5;

  // Watch package details and invalidate rates once the customer stops typing.
  // Without the delay this fires on every keystroke, so entering "50" invalidates
  // at "5" and again at "50" - two toasts and the re-quote step flickering in.
  useEffect(() => {
    // Skip on initial load or if rates haven't been fetched yet
    if (!lastPackageSnapshot || priceUpdateStatus === 'updating') {
      return;
    }

    const timer = setTimeout(() => {
      if (hasPackageDetailsChanged()) {
        console.log('📦 Package details settled - invalidating carrier rates');
        invalidateRates();
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [bookingData.packageBoxes]); // Monitor package boxes for changes

  // Centralized function to calculate updated pricing based on package details - DEPRECATED, use pricingInfo instead
  const calculateUpdatedPricing = () => {
    console.warn('⚠️ calculateUpdatedPricing called - use memoized pricingInfo instead');
    return pricingInfo;
  };

  // Debug: Log savedAddresses whenever it changes
  useEffect(() => {
    console.log('SavedAddresses state updated:', savedAddresses);
  }, [savedAddresses]);

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      // Cleanup any pending timeout
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
    };
  }, [updateTimeout]);

  // Set recipient country based on selected quote
  useEffect(() => {
    if (selectedQuote && selectedQuote.to_country) {
      // Map country codes to country names if needed
      const countryMapping = {
        'US': 'United States',
        'UK': 'United Kingdom',
        'CA': 'Canada',
        'AU': 'Australia',
        'DE': 'Germany',
        'FR': 'France',
        'IT': 'Italy',
        'ES': 'Spain',
        'NL': 'Netherlands',
        'BE': 'Belgium',
        'CH': 'Switzerland',
        'AT': 'Austria',
        'SE': 'Sweden',
        'NO': 'Norway',
        'DK': 'Denmark',
        'FI': 'Finland',
        'JP': 'Japan',
        'SG': 'Singapore',
        'HK': 'Hong Kong',
        'AE': 'United Arab Emirates',
        'SA': 'Saudi Arabia',
        'DZ': 'Algeria',
        'AL': 'Albania'
      };

      const countryName = countryMapping[selectedQuote.to_country] || selectedQuote.to_country;

      // Debug logging for country mapping
      console.log('🌍 Country Mapping Debug:', {
        selectedQuoteToCountry: selectedQuote.to_country,
        mappedCountryName: countryName,
        countryMapping: countryMapping[selectedQuote.to_country]
      });

      setBookingData(prev => ({
        ...prev,
        recipientCountry: countryName
      }));
    }
  }, [selectedQuote]);

  // Initialize package details from quote data
  useEffect(() => {
    if (selectedQuote && selectedQuote.weight) {
      console.log('📦 Initializing package details from quote:', {
        weight: selectedQuote.weight,
        length: selectedQuote.length,
        width: selectedQuote.width,
        height: selectedQuote.height,
        declared_value: selectedQuote.declared_value
      });

      // Only initialize if packageBoxes is empty or has default values
      const hasEmptyPackageBox = bookingData.packageBoxes.length === 1 && 
        !bookingData.packageBoxes[0].weight && 
        !bookingData.packageBoxes[0].length;

      const hasEmptyContentItem = bookingData.contentItems.length === 1 && 
        !bookingData.contentItems[0].value;

      if (hasEmptyPackageBox || hasEmptyContentItem) {
        const newPackageBoxes = hasEmptyPackageBox ? [{
          id: 1,
          weight: selectedQuote.weight || '',
          length: selectedQuote.length || '',
          width: selectedQuote.width || '',
          height: selectedQuote.height || ''
        }] : bookingData.packageBoxes;

        setBookingData(prev => ({
          ...prev,
          packageBoxes: newPackageBoxes,
          contentItems: hasEmptyContentItem && selectedQuote.declared_value ? [{
            id: 1,
            description: '',
            quantity: 1,
            value: selectedQuote.declared_value || '',
            hsnCode: ''
          }] : prev.contentItems
        }));

        // Set rates as valid since package details match the quote
        // Create snapshot of the initialized package details
        const initialSnapshot = {
          packageBoxes: newPackageBoxes.map(box => ({
            weight: box.weight,
            length: box.length,
            width: box.width,
            height: box.height
          })),
          packageCount: newPackageBoxes.length
        };
        
        setLastPackageSnapshot(initialSnapshot);
        setRatesValid(true);

        console.log('✅ Package details and content value initialized from quote');
        console.log('✅ Rates marked as valid - package details match quote');
      }
    }
  }, [selectedQuote]);

  // Effect to ensure proper state when entering step 3
  useEffect(() => {
    if (currentStep === 3) {
      console.log('📍 Entered step 3 - checking rates state:', {
        selectedQuote: !!selectedQuote,
        alternativeQuotes: alternativeQuotes.length,
        ratesValid,
        lastPackageSnapshot: !!lastPackageSnapshot
      });

      // If we have a valid snapshot and rates are valid (from quote initialization),
      // don't invalidate even if alternativeQuotes is empty
      // This allows users to proceed directly if package details haven't changed
      if (ratesValid && lastPackageSnapshot) {
        console.log('✅ Rates are valid from quote - allowing direct proceed');
        return;
      }

      // If no rates have been fetched yet (fresh visit to step 3 without quote initialization)
      if (!selectedQuote || !lastPackageSnapshot) {
        console.log('📦 No rates fetched yet - invalidating state');
        setRatesValid(false);
        setLastPackageSnapshot(null);
        setAlternativeQuotes([]);
        setShowAlternatives(false);
        setRecommendedAlternative(null);
      }
    }
  }, [currentStep, selectedQuote, alternativeQuotes.length, lastPackageSnapshot, ratesValid]);

  // Auto-update quotes when package boxes change (weight/dimensions changes) - DISABLED to prevent multiple triggers
  useEffect(() => {
    const updateQuoteForPackageChanges = async () => {
      // Only update if we have package boxes and a quote ID
      if (!bookingData.packageBoxes.length || !quoteId) return;

      // Calculate new total weight from package boxes
      const totalWeight = bookingData.packageBoxes.reduce((total, box) => {
        return total + (parseFloat(box.weight) || 0);
      }, 0);

      // Calculate chargeable weight for each box individually, then sum
      const totalChargeableWeight = bookingData.packageBoxes.reduce((total, box) => {
        const actualWeight = parseFloat(box.weight) || 0;
        const length = parseFloat(box.length) || 0;
        const width = parseFloat(box.width) || 0;
        const height = parseFloat(box.height) || 0;
        
        // Calculate volumetric weight for this box
        const volumetricWeight = (length && width && height) ? (length * width * height) / 5000 : 0;
        
        // Chargeable weight is the higher of actual or volumetric for this box
        const chargeableWeight = Math.max(actualWeight, volumetricWeight);
        
        return total + chargeableWeight;
      }, 0);

      // Calculate total value from content items
      const totalValue = bookingData.contentItems.reduce((total, item) => {
        const value = parseFloat(item.value) || 0;
        const quantity = parseInt(item.quantity) || 1;
        return total + (value * quantity);
      }, 0);

      // Just log the package updates without triggering refresh - NO AUTO REFRESH
      console.log('📦 Package updated - Actual Weight:', totalWeight, 'kg, Chargeable:', totalChargeableWeight.toFixed(2), 'kg, Value: ₹', totalValue);
    };

    updateQuoteForPackageChanges();
  }, []); // REMOVED dependencies to prevent auto-refresh - only manual refresh now

  // Manual quote refresh function - triggered by button click only
  const handleManualQuoteRefresh = async () => {
    console.log('🔄 Manual quote refresh triggered by user');
    setPriceUpdateStatus('updating');
    setShowAlternatives(false); // Hide previous recommendations

    // Reset status to idle first, then to updating for clean state
    setTimeout(() => {
      setPriceUpdateStatus('updating');
    }, 100);

    await refreshQuotesForPackageChanges();

    // Validate rates after successful refresh
    validateRates();
  };
  // The request a re-quote sends for the parcel as entered. Also used to
  // store a quote before payment when the page has none.
  const buildRequoteRequest = () => {
    // Calculate total weight from package boxes
    // Calculate total actual weight
    const totalWeight = bookingData.packageBoxes.reduce((total, box) => {
      return total + (parseFloat(box.weight) || 0);
    }, 0);

    // Calculate chargeable weight for each box individually, then sum
    const totalChargeableWeight = bookingData.packageBoxes.reduce((total, box) => {
      const actualWeight = parseFloat(box.weight) || 0;
      const length = parseFloat(box.length) || 0;
      const width = parseFloat(box.width) || 0;
      const height = parseFloat(box.height) || 0;
      
      // Calculate volumetric weight for this box
      const volumetricWeight = (length && width && height) ? (length * width * height) / 5000 : 0;
      
      // Chargeable weight is the higher of actual or volumetric for this box
      const chargeableWeight = Math.max(actualWeight, volumetricWeight);
      
      return total + chargeableWeight;
    }, 0);

    // Use the largest box dimensions for display purposes only
    const maxDimensions = bookingData.packageBoxes.reduce((max, box) => {
      const length = parseFloat(box.length) || 0;
      const width = parseFloat(box.width) || 0;
      const height = parseFloat(box.height) || 0;

      return {
        length: Math.max(max.length, length),
        width: Math.max(max.width, width),
        height: Math.max(max.height, height)
      };
    }, { length: 0, width: 0, height: 0 });

    // Calculate total declared value
    const totalValue = bookingData.contentItems.reduce((total, item) => {
      return total + (parseFloat(item.value) || 0);
    }, 0);

    // EXACT SAME REQUEST FORMAT AS HERO
    const quoteRequest = {
      from_country: 'IN',
      to_country: selectedQuote.to_country || bookingData.recipientCountry || 'ALGERIA', // Use recipient country as fallback
      // A state or postcode rate stays a state or postcode rate when re-priced.
      to_postal_code: selectedQuote.to_postal_code || null,
      weight: Math.max(totalChargeableWeight || 0.5, 0.1), // Use chargeable weight for accurate pricing
      shipment_type: selectedQuote.shipment_type || 'parcel',
      declared_value: Math.max(totalValue || 0, 0), // Ensure non-negative value
      // Don't send dimensions when we have multiple packages - we've already calculated chargeable weight
      length: bookingData.packageBoxes.length === 1 && maxDimensions.length > 0 ? Math.max(maxDimensions.length, 1) : null,
      width: bookingData.packageBoxes.length === 1 && maxDimensions.width > 0 ? Math.max(maxDimensions.width, 1) : null,
      height: bookingData.packageBoxes.length === 1 && maxDimensions.height > 0 ? Math.max(maxDimensions.height, 1) : null,
      packages: [{
        id: 1,
        weight: Math.max(totalWeight || 0.5, 0.1),
        length: maxDimensions.length > 0 ? Math.max(maxDimensions.length, 1) : (selectedQuote.shipment_type === 'document' ? 21 : 30),
        width: maxDimensions.width > 0 ? Math.max(maxDimensions.width, 1) : (selectedQuote.shipment_type === 'document' ? 29.7 : 20),
        height: maxDimensions.height > 0 ? Math.max(maxDimensions.height, 1) : (selectedQuote.shipment_type === 'document' ? 1 : 10),
        quantity: 1
      }],
      content_items: [{
        id: 1,
        description: bookingData.contentItems[0]?.description || 'General Items',
        quantity: Math.max(1, parseInt(bookingData.contentItems[0]?.quantity) || 1),
        value: Math.max(totalValue || (selectedQuote.shipment_type === 'document' ? 500 : 100), 0),
        hsn_code: bookingData.contentItems[0]?.hsnCode || ''
      }]
    };
    return { quoteRequest, totalWeight, totalChargeableWeight, maxDimensions };
  };

  // Function to refresh quotes when package details change - EXACT SAME AS HERO
  const refreshQuotesForPackageChanges = async () => {
    // Prevent multiple simultaneous refresh calls
    if (refreshInProgress) {
      console.log('🚫 Refresh already in progress, skipping...');
      return;
    }

    console.log('🔄 Starting quote refresh with EXACT SAME API as Hero...');
    setRefreshInProgress(true);

    try {
      const { quoteRequest, totalWeight, totalChargeableWeight, maxDimensions } = buildRequoteRequest();

      console.log('🔄 Quote refresh with corrected weight calculation:', {
        totalActualWeight: totalWeight,
        totalChargeableWeight: totalChargeableWeight,
        packageCount: bookingData.packageBoxes.length,
        weight: quoteRequest.weight,
        length: quoteRequest.length,
        width: quoteRequest.width,
        height: quoteRequest.height,
        shipment_type: quoteRequest.shipment_type,
        declared_value: quoteRequest.declared_value,
        to_country: quoteRequest.to_country
      });

      // Validate request before sending
      if (!quoteRequest.to_country || !quoteRequest.shipment_type || quoteRequest.weight <= 0) {
        throw new Error(`Invalid request data: to_country=${quoteRequest.to_country}, shipment_type=${quoteRequest.shipment_type}, weight=${quoteRequest.weight}`);
      }

      console.log('📤 Sending quote request:', JSON.stringify(quoteRequest, null, 2));

      // EXACT SAME API CALL AS HERO
      const response = await quotesAPI.createQuote(quoteRequest);

      if (response && response.carrier_quotes) {
        // Each option remembers the stored quote it came from, so the server
        // can price the booking from the quote the customer actually chose.
        response.carrier_quotes = response.carrier_quotes.map(quote => ({ ...quote, quote_id: response.id }));
        // Find the matching carrier quote for current selection
        const matchingQuote = response.carrier_quotes.find(quote =>
          quote.carrier_name === selectedQuote.carrier_name
        );

        setFreshQuotes(response.carrier_quotes);

        // Find alternative carriers (excluding current selection)
        const alternatives = response.carrier_quotes.filter(quote =>
          quote.carrier_name !== selectedQuote.carrier_name
        ).sort((a, b) => a.total_cost - b.total_cost); // Sort by price

        // Find the best alternative (cheapest)
        const bestAlternative = alternatives.length > 0 ? alternatives[0] : null;

        // Check if there's a significantly better alternative (at least 10% cheaper)
        const currentCost = matchingQuote ? matchingQuote.total_cost : selectedQuote.total_cost;
        const showRecommendation = bestAlternative &&
          ((currentCost - bestAlternative.total_cost) / currentCost) >= 0.1; // 10% or more savings

        if (matchingQuote) {
          // Update the selected quote with fresh pricing - EXACT SAME DATA AS API
          const updatedQuote = {
            ...selectedQuote,
            ...matchingQuote, // Use ALL data from API response
            // Preserve original quote data that shouldn't change
            to_country: selectedQuote.to_country,
            shipment_type: selectedQuote.shipment_type
          };

          // Update the selected quote via callback if available
          if (onQuoteUpdate) {
            onQuoteUpdate(updatedQuote);
          }

          console.log('✅ Updated quote with EXACT API DATA:', {
            carrier: matchingQuote.carrier_name,
            original_cost: selectedQuote.total_cost,
            new_cost: matchingQuote.total_cost,
            api_weight: matchingQuote.weight,
            api_volumetric: matchingQuote.volumetric_weight,
            api_chargeable: matchingQuote.chargeable_weight,
            dimensions: `${maxDimensions.length}×${maxDimensions.width}×${maxDimensions.height}cm`,
            alternatives_found: alternatives.length,
            best_alternative: bestAlternative ? `${bestAlternative.carrier_name} - ₹${bestAlternative.total_cost}` : 'None',
            show_recommendation: showRecommendation
          });

          // Update alternative quotes state
          setAlternativeQuotes(alternatives);
          setRecommendedAlternative(showRecommendation ? bestAlternative : null);
          setShowAlternatives(showRecommendation);

          setPriceUpdateStatus('updated');

          // DON'T auto-reset to idle - let user see results until next action
          // Removed the setTimeout that was causing auto-vanishing
        } else {
          console.warn('⚠️ No matching carrier found in refreshed quotes');
          setPriceUpdateStatus('idle');
        }
      } else {
        console.warn('⚠️ No quotes received from API');
        setPriceUpdateStatus('idle');
      }

    } catch (error) {
      console.error('❌ Quote refresh error:', error);
      console.error('❌ Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });

      // Show user-friendly error message
      toast({
        title: "Quote Refresh Failed",
        description: error.response?.data?.detail || "Failed to refresh quotes. Please try again.",
        variant: "destructive"
      });

      setPriceUpdateStatus('idle');
    } finally {
      setRefreshInProgress(false);
    }
  };

  // Returns the figures as well as storing them, so a payment can be worked
  // out from the wallet as it is now rather than when the page loaded.
  const refreshWalletBalance = async () => {
    try {
      const response = await api.get('/payments/wallet/balance');
      setWalletBalance(response.data.data);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch wallet balance:', error);
      setWalletBalance(null);
      return null;
    }
  };

  // Fetch saved addresses on mount
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        // Fetch from address-book endpoint (where addresses are actually saved)
        const response = await api.get('/address-book/addresses');
        const addresses = response.data;

        console.log('Fetched addresses:', addresses);
        console.log('Number of addresses:', addresses?.length || 0);

        if (addresses && Array.isArray(addresses)) {
          setSavedAddresses(addresses);
        } else {
          console.warn('Addresses is not an array:', addresses);
          setSavedAddresses([]);
        }
      } catch (error) {
        console.error('Failed to fetch addresses:', error);
        setSavedAddresses([]);
      }
    };

    const fetchKYCStatus = async () => {
      try {
        const response = await api.get('/kyc/status');
        setKycStatus(response.data);
      } catch (error) {
        console.error('Failed to fetch KYC status:', error);
        setKycStatus(null);
      }
    };

    const fetchUserDiscount = async () => {
      try {
        const response = await api.get('/bookings/my-discount');
        setUserDiscount(response.data.discount || null);
      } catch {
        setUserDiscount(null);
      }
    };

    if (user) {
      fetchAddresses();
      fetchKYCStatus();
      refreshWalletBalance();
      fetchUserDiscount();
    }
  }, [user]);

  const steps = [
    { id: 1, title: 'Sender Details', icon: <User className="w-5 h-5" /> },
    { id: 10, title: 'Pickup or Drop-off', icon: <Truck className="w-5 h-5" /> },
    { id: 2, title: 'Recipient Details', icon: <MapPin className="w-5 h-5" /> },
    { id: 3, title: 'Shipment Boxes', icon: <Package className="w-5 h-5" /> },
    { id: 6, title: 'Shipment Contents & Value', icon: <FileText className="w-5 h-5" /> },
    { id: 7, title: 'Special Instructions', icon: <AlertTriangle className="w-5 h-5" /> },
    { id: 8, title: 'Updated Rates', icon: <Truck className="w-5 h-5" /> },
    { id: 4, title: 'Review & Confirm', icon: <CheckCircle className="w-5 h-5" /> },
    { id: 9, title: 'Payment', icon: <CreditCard className="w-5 h-5" /> },
    { id: 5, title: 'Confirmation', icon: <CheckCircle className="w-5 h-5" /> }
  ];

  // Visiting order: parcel size/contents come straight after the quote, since
  // they can change the price; addresses follow. Ids stay stable so validation
  // and step rendering are untouched.
  // Dimensions can change the chargeable weight, which changes the price.
  // When that happens the customer re-picks a carrier at the new weight before
  // going any further, so an extra step appears right after the boxes.
  const isDocument = selectedQuote?.shipment_type === 'document';
  const stepOrder = (requotePending
    ? [3, 8, 6, 7, 1, 10, 2, 4, 9, 5]
    : [3, 6, 7, 1, 10, 2, 4, 9, 5]
  ).filter((id) => !(id === 3 && isDocument));
  const orderIndex = (id) => stepOrder.indexOf(id);
  // Build strictly from stepOrder, so steps not in it (e.g. the re-quote step
  // when rates are still valid) are not rendered at all.
  const stepByOrder = stepOrder.map((id) => steps.find((st) => st.id === id)).filter(Boolean);

  // currentStep initialises to the boxes step, which does not exist for
  // document bookings. Without this the page renders with every section
  // collapsed and no way to continue.
  useEffect(() => {
    if (currentStep !== 5 && !stepOrder.includes(currentStep)) {
      setCurrentStep(stepOrder[0]);
    }
  }, [stepOrder, currentStep]);

  const handleInputChange = (field, value) => {
    setBookingData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Content items management functions
  const addContentItem = () => {
    setBookingData(prev => ({
      ...prev,
      contentItems: [...prev.contentItems, {
        id: Date.now(),
        description: '',
        quantity: 1,
        value: '',
        hsnCode: ''
      }]
    }));
  };

  const removeContentItem = (id) => {
    if (bookingData.contentItems.length > 1) {
      setBookingData(prev => ({
        ...prev,
        contentItems: prev.contentItems.filter(item => item.id !== id)
      }));
    }
  };

  const updateContentItem = (id, field, value) => {
    setBookingData(prev => ({
      ...prev,
      contentItems: prev.contentItems.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  };

  // Package boxes management functions
  const addPackageBox = () => {
    setBookingData(prev => ({
      ...prev,
      packageBoxes: [...prev.packageBoxes, {
        id: Date.now(),
        weight: '',
        length: '',
        width: '',
        height: ''
      }]
    }));
  };

  const removePackageBox = (id) => {
    if (bookingData.packageBoxes.length > 1) {
      setBookingData(prev => ({
        ...prev,
        packageBoxes: prev.packageBoxes.filter(box => box.id !== id)
      }));
    }
  };

  const updatePackageBox = (id, field, value) => {
    console.log(`📝 updatePackageBox called: ${field} = ${value}`);

    setBookingData(prev => ({
      ...prev,
      packageBoxes: prev.packageBoxes.map(box =>
        box.id === id ? { ...box, [field]: value } : box
      )
    }));

    // Keep pricing status visible when dimensions change - don't auto-close
    // User requested that the update pricing section should not auto-close
    console.log('📦 Dimensions changed - keeping pricing status visible for user convenience');
  };

  const loadSavedAddress = (addressId, type) => {
    const address = savedAddresses.find(addr => addr.id === addressId);
    if (!address) {
      console.log('Address not found:', addressId);
      return;
    }

    console.log('Loading address:', address);

    const prefix = type === 'sender' ? 'sender' : 'recipient';
    setBookingData(prev => ({
      ...prev,
      [`${prefix}Name`]: address.name || '',
      [`${prefix}Phone`]: address.phone || '',
      [`${prefix}Email`]: address.email || '',
      [`${prefix}Company`]: address.company || '',
      [`${prefix}AddressLine1`]: address.street || address.address_line_1 || '',
      [`${prefix}AddressLine2`]: address.address_line_2 || '',
      [`${prefix}City`]: address.city || '',
      [`${prefix}PostalCode`]: address.postal_code || '',
      [`${prefix}Landmark`]: address.landmark || '',
      [`${prefix}Country`]: address.country || 'India'
    }));

    toast({
      title: "Address Loaded",
      description: `${address.label || 'Address'} loaded successfully`
    });
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        // Email is now optional - removed from validation
        const senderBasicValid = bookingData.senderName && bookingData.senderPhone &&
          bookingData.senderAddressLine1 && bookingData.senderCity &&
          bookingData.senderPostalCode;

        if (!senderBasicValid) {
          return false;
        }

        return senderBasicValid;

      case 10:
        // Door pickup needs a priced pickup PIN code; drop-off always works.
        return pickupMethod === 'drop_off' || pickupQuote.status === 'ok';

      case 2:
        // Email is now optional - removed from validation
        const recipientBasicValid = bookingData.recipientName && bookingData.recipientPhone &&
          bookingData.recipientAddressLine1 && bookingData.recipientCity &&
          bookingData.recipientPostalCode;

        if (!recipientBasicValid) {
          return false;
        }

        return recipientBasicValid;

      case 3: {
        // Boxes: weight + dimensions on every box
        const invalidPackage = bookingData.packageBoxes.find(box =>
          !box.weight || !box.length || !box.width || !box.height
        );
        return !invalidPackage;
      }
      case 6: {
        // Contents: description, quantity and value on every item
        const invalidContent = bookingData.contentItems.find(item =>
          !item.description || !item.quantity || !item.value
        );
        return !invalidContent;
      }
      case 7:
        // Special instructions are optional
        return true;
      case 8:
        // Must refresh (and thereby re-price) before continuing
        return ratesValid;
      case 4:
        // Details were checked earlier, but the parcel may have changed since
        // the quote was priced - paying then would charge the wrong amount.
        return !weightMismatch;
      case 9: {
        // Validate terms and conditions acceptance AND payment method selection
        const paymentMethodValid = selectedPaymentMethod &&
          (selectedPaymentMethod === 'razorpay' || selectedPaymentMethod === 'partial' || selectedPaymentMethod === 'wallet');
        return termsAccepted && paymentMethodValid;
      }
      default:
        return true;
    }
  };

  const handleNext = () => {
    // Special validation for step 3 (Package Details) - ENHANCED VALIDATION
    if (currentStep === 3) {
      // Check if package details exist (basic validation)
      if (!validateStep(currentStep)) {
        toast({
          title: "Missing Information",
          description: "Please fill in all content item details (description, quantity, and value) and package box details (weight and dimensions).",
          variant: "destructive"
        });
        return;
      }

      // Stale rates are not a dead end: the very next step IS the re-quote step,
      // so let the customer through to it instead of blocking them here.
      if (!ratesValid && lastPackageSnapshot) {
        setRequotePending(true);
        setCurrentStep(8);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      // Ensure we have a selected quote
      if (!selectedQuote) {
        toast({
          title: "Carrier Required",
          description: "Please select a carrier before proceeding.",
          variant: "destructive"
        });
        return;
      }

      // Additional validation: Ensure rates are fresh for current package details
      const currentPackageDetails = {
        totalWeight: bookingData.packageBoxes.reduce((sum, box) => sum + parseFloat(box.weight || 0), 0),
        totalValue: bookingData.contentItems.reduce((sum, item) => sum + (parseFloat(item.value || 0) * parseInt(item.quantity || 1)), 0),
        dimensions: bookingData.packageBoxes.map(box => ({
          length: parseFloat(box.length || 0),
          width: parseFloat(box.width || 0),
          height: parseFloat(box.height || 0)
        })),
        packageCount: bookingData.packageBoxes.length,
        contentCount: bookingData.contentItems.length
      };
    }

    // The parcel changed after the quote was priced. The re-quote step is the
    // way forward, not a toast the customer cannot act on.
    if (currentStep === 4 && weightMismatch) {
      setRequotePending(true);
      setCurrentStep(8);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Standard validation for other steps
    if (!validateStep(currentStep)) {
      let errorMessage = "Please fill in all required fields.";

      if (currentStep === 1) {
        errorMessage = "Please fill in all sender details including name, phone, address, city, and postal code.";
      } else if (currentStep === 10) {
        errorMessage = {
          idle: "Pickup needs the 6-digit PIN code of your address. Fix it in Sender Details, or choose to drop the parcel at our office.",
          loading: "Checking pickup at your PIN code - one moment.",
          unavailable: "Pickup isn't available at this PIN code. Choose to drop the parcel at our office instead.",
          error: "We couldn't check pickup just now. Try again, or choose to drop the parcel at our office.",
        }[pickupQuote.status] || "Choose pickup or drop-off.";
      } else if (currentStep === 2) {
        errorMessage = "Please fill in all recipient details including name, phone, address, city, and postal code.";
      } else if (currentStep === 9) {
        if (!termsAccepted && (!selectedPaymentMethod || (selectedPaymentMethod !== 'razorpay' && selectedPaymentMethod !== 'partial' && selectedPaymentMethod !== 'wallet'))) {
          errorMessage = "Please accept the terms and conditions and select a payment method to proceed.";
        } else if (!termsAccepted) {
          errorMessage = "Please accept the terms and conditions to proceed.";
        } else if (!selectedPaymentMethod || (selectedPaymentMethod !== 'razorpay' && selectedPaymentMethod !== 'partial' && selectedPaymentMethod !== 'wallet')) {
          errorMessage = "Please select a payment method to proceed.";
        }
      }

      toast({
        title: "Missing Information",
        description: errorMessage,
        variant: "destructive"
      });
      return;
    }

    // Sign-in is required only from the payment step onwards.
    if (stepOrder[orderIndex(currentStep) + 1] === 9 && !user) {
      setShowRegisterModal(true);
      toast({
        title: "Almost there",
        description: "Create an account (or sign in) to pay and confirm your booking. Your details are saved."
      });
      return;
    }

    if (currentStep === 8) {
      setRequotePending(false);
    }

    const nextStep = stepOrder[orderIndex(currentStep) + 1];
    if (nextStep) {
      setCurrentStep(nextStep);
      // Scroll to top of page smoothly
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    if (orderIndex(currentStep) > 0) {
      console.log(`📍 Navigating from step ${currentStep} to step ${currentStep - 1}`);
      console.log('📊 Current booking data state:', {
        packageBoxes: bookingData.packageBoxes.length,
        contentItems: bookingData.contentItems.length,
        recipientCountry: bookingData.recipientCountry,
        selectedQuote: selectedQuote?.carrier_name
      });
      setCurrentStep(stepOrder[orderIndex(currentStep) - 1]);
      // Scroll to top of page smoothly
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Auto-save addresses after successful booking
  const autoSaveAddresses = async (senderData, recipientData) => {
    try {
      console.log('🔄 Auto-saving addresses...', { senderData, recipientData });
      
      // Check if sender address already exists
      const existingAddresses = await api.get('/address-book/addresses');
      const addresses = existingAddresses.data || [];

      // Helper function to check if address already exists
      const addressExists = (newAddr, type) => {
        return addresses.some(addr => 
          addr.address_type === type &&
          addr.name === newAddr.name &&
          addr.phone === newAddr.phone &&
          addr.street === newAddr.street &&
          addr.city === newAddr.city &&
          addr.postal_code === newAddr.postal_code
        );
      };

      // Save sender address if it doesn't exist
      if (!addressExists(senderData, 'sender')) {
        const senderAddress = {
          label: senderData.company || senderData.name || 'Sender Address',
          address_type: 'pickup',  // Changed from 'sender' to 'pickup'
          category: 'other',  // Changed from 'business' to 'other'
          name: senderData.name,
          company: senderData.company || '',
          phone: senderData.phone,
          email: senderData.email || null,
          street: senderData.street,
          city: senderData.city,
          state: senderData.state || senderData.city,
          postal_code: senderData.postal_code,
          country: senderData.country || 'India',
          landmark: senderData.landmark || '',
          is_default_pickup: false,
          is_default_delivery: false
        };
        
        console.log('📤 Sending sender address:', JSON.stringify(senderAddress, null, 2));
        try {
          await api.post('/address-book/addresses', senderAddress);
          console.log('✅ Sender address auto-saved');
        } catch (err) {
          console.error('❌ Sender address save failed:', err.response?.data);
          throw err;
        }
      } else {
        console.log('ℹ️ Sender address already exists, skipping save');
      }

      // Save recipient address if it doesn't exist
      if (!addressExists(recipientData, 'recipient')) {
        const recipientAddress = {
          label: recipientData.company || recipientData.name || 'Recipient Address',
          address_type: 'delivery',  // Changed from 'recipient' to 'delivery'
          category: 'other',  // Changed from 'business' to 'other'
          name: recipientData.name,
          company: recipientData.company || '',
          phone: recipientData.phone,
          email: recipientData.email || null,
          street: recipientData.street,
          city: recipientData.city,
          state: recipientData.state || recipientData.city,
          postal_code: recipientData.postal_code,
          country: recipientData.country || 'India',
          landmark: recipientData.landmark || '',
          is_default_pickup: false,
          is_default_delivery: false
        };
        
        console.log('📤 Sending recipient address:', JSON.stringify(recipientAddress, null, 2));
        await api.post('/address-book/addresses', recipientAddress);
        console.log('✅ Recipient address auto-saved');
      } else {
        console.log('ℹ️ Recipient address already exists, skipping save');
      }

      // Refresh saved addresses list
      fetchSavedAddresses();
      
      toast({
        title: "Addresses Saved",
        description: "Sender and recipient addresses have been saved for future use.",
      });
    } catch (error) {
      console.error('❌ Error auto-saving addresses:', error);
      console.error('Error response:', error.response?.data);
      // Don't show error toast to avoid annoying users
    }
  };

  const createBooking = async () => {
    // A fresh attempt clears any previous payment error.
    setPaymentError(null);
    // Prevent multiple submissions
    if (loading || paymentProcessing) {
      return;
    }

    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to create a booking.",
        variant: "destructive"
      });
      return;
    }

    // Check terms and conditions acceptance before proceeding
    if (!termsAccepted) {
      setShowTermsError(true);

      // Scroll to terms checkbox
      const termsElement = document.getElementById('terms');
      if (termsElement) {
        termsElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        // Focus on the checkbox
        setTimeout(() => {
          termsElement.focus();
        }, 500);
      }

      toast({
        title: "Terms and Conditions Required",
        description: "Please accept the terms and conditions to proceed with booking.",
        variant: "destructive"
      });
      return;
    }

    // CRITICAL: Validate payment method selection
    if (!selectedPaymentMethod || (selectedPaymentMethod !== 'razorpay' && selectedPaymentMethod !== 'partial' && selectedPaymentMethod !== 'wallet')) {
      toast({
        title: "Payment Method Required",
        description: "Please select a payment method to proceed with booking.",
        variant: "destructive"
      });

      // Scroll to payment method section
      const paymentSection = document.querySelector('[data-payment-section]');
      if (paymentSection) {
        paymentSection.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
      return;
    }

    // Reset terms error if terms are accepted
    setShowTermsError(false);
    setPaymentProcessing(true);

    try {
      // The booking is written to the server BEFORE any money moves. If this
      // fails, the customer is not charged at all - which is the right way
      // round. Previously payment came first and a failure here took the money
      // with nothing to show for it.
      try {
        await ensureDraftBooking();
      } catch (draftError) {
        console.error('Could not create the booking before payment:', draftError);
        setPaymentProcessing(false);
        setLoading(false);
        // 409: the price moved on (quote expired, or the rate changed). The
        // server says which; the customer needs a fresh quote, not a retry.
        const priceDetail = draftError?.priceChanged
          ? draftError.message
          : draftError?.response?.status === 409 && typeof draftError.response.data?.detail === 'string'
            ? draftError.response.data.detail
            : null;
        const description = priceDetail
          ? `${priceDetail} We have not taken any payment.`
          : "We could not save your booking, so we have not taken any payment. Please try again in a moment.";
        setPaymentError(description);
        toast({
          title: "Could not start your booking",
          description,
          variant: "destructive",
        });
        return;
      }

      // Handle different payment methods - ALL require payment processing
      if (selectedPaymentMethod === 'wallet') {
        await handleWalletPayment();
        return;
      } else if (selectedPaymentMethod === 'razorpay') {
        await handleRazorpayPayment();
        return;
      } else if (selectedPaymentMethod === 'partial') {
        // For partial payment, process with 10% amount
        await handleRazorpayPayment(true); // Pass partial flag
        return;
      }

      // REMOVED: No fallback to direct booking creation without payment
      // This ensures all bookings go through proper payment validation
      toast({
        title: "Payment Processing Error",
        description: "Invalid payment method selected. Please try again.",
        variant: "destructive"
      });
    } catch (error) {
      console.error('Payment processing error:', error);
      toast({
        title: "Payment Error",
        description: "Failed to process payment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleWalletPayment = async () => {
    setLoading(true);
    setPaymentProcessing(true);

    try {
      // Worked out from the booking's server price and the wallet as it is
      // now. The wallet money itself is taken by the server when it confirms
      // the booking, so nothing leaves the wallet unless the booking is made.
      const price = Number(draftRef.current?.final_cost ?? pricingInfo.totalCost);
      const wallet = await refreshWalletBalance();
      // Never plan to take more than the wallet may spend today: the online
      // part is charged first, and the wallet part could then be refused.
      const walletAmt = Math.min(
        Number(wallet?.balance || 0),
        wallet?.daily_remaining != null ? Number(wallet.daily_remaining) : Infinity
      );
      if (!wallet || !(walletAmt > 0)) {
        throw new Error(wallet && Number(wallet.balance) > 0
          ? "Your wallet has reached today's spending limit. Please choose Pay Online."
          : 'Your wallet balance is not available right now. Please choose another payment method.');
      }
      const onlineAmt = Math.round(Math.max(0, price - walletAmt) * 100) / 100;

      if (onlineAmt === 0) {
        await processBookingCreation({ payment_method: 'wallet' });
        return;
      }

      // Wallet plus online: the online part is paid first, and the wallet
      // part is only taken once that has gone through.
      const orderResponse = await api.post('/payments/create-order', {
        amount: onlineAmt,
        currency: 'INR',
        receipt: `booking_${Date.now()}`,
        notes: {
          booking_type: 'shipping',
          carrier: selectedQuote.carrier_name,
          user_id: user.id,
          payment_type: 'wallet_split',
          shipment_id: draftRef.current?.id,
          shipment_number: draftRef.current?.shipment_number,
          total_amount: price,
          wallet_amount: Math.round((price - onlineAmt) * 100) / 100,
          razorpay_amount: onlineAmt
        }
      });

      await handleDirectPayment(orderResponse.data, false);
    } catch (error) {
      console.error('Wallet payment error:', error);
      setLoading(false);
      setPaymentProcessing(false);
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (error.message || "Wallet payment failed. Please try again.");
      setPaymentError(message);
      toast({
        title: "Payment Error",
        description: message,
        variant: "destructive"
      });
    }
  };

  const handleRazorpayPayment = async (isPartial = false) => {
    // Check if mock payments are enabled
    const useMockPayments = process.env.REACT_APP_USE_MOCK_PAYMENTS === 'true';

    if (useMockPayments) {
      // Handle mock payment
      setLoading(true);
      setPaymentProcessing(true);

      try {
        // Simulate payment processing delay
        await new Promise(resolve => setTimeout(resolve, 2000));

        // A development backend (USE_MOCK_PAYMENTS) creates mock orders and
        // accepts mock payments for them - and only for them.
        const mockPrice = Number(draftRef.current?.final_cost ?? pricingInfo.totalCost);
        const mockAmount = isPartial ? Math.round(mockPrice * 10) / 100 : mockPrice;
        const mockOrder = await api.post('/payments/create-order', {
          amount: mockAmount,
          currency: 'INR',
          notes: { payment_type: isPartial ? 'partial' : 'full', shipment_id: draftRef.current?.id }
        });
        const mockPaymentId = `pay_mock_${Date.now()}`;

        await processBookingCreation({
          payment_method: isPartial ? 'partial' : 'razorpay',
          razorpay_order_id: mockOrder.data.order_id,
          razorpay_payment_id: mockPaymentId,
          razorpay_signature: 'mock_signature_development',
        });

      } catch (error) {
        console.error('Mock payment error:', error);
        toast({
          title: "Mock Payment Failed",
          description: error.message || "Mock payment processing failed",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
        setPaymentProcessing(false);
      }
      return;
    }
    // Double-check terms acceptance before payment
    if (!termsAccepted) {
      toast({
        title: "Terms and Conditions Required",
        description: "Please accept the terms and conditions before proceeding with payment.",
        variant: "destructive"
      });
      return;
    }

    // Double-check payment method selection
    if (!selectedPaymentMethod || (selectedPaymentMethod !== 'razorpay' && selectedPaymentMethod !== 'partial' && selectedPaymentMethod !== 'wallet')) {
      toast({
        title: "Payment Method Required",
        description: "Please select a valid payment method before proceeding.",
        variant: "destructive"
      });
      return;
    }

    // Validate payment method matches the function call
    if ((isPartial && selectedPaymentMethod !== 'partial') || (!isPartial && selectedPaymentMethod !== 'razorpay')) {
      toast({
        title: "Payment Method Mismatch",
        description: "Selected payment method does not match the processing method.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    try {
      // The draft carries the price the server will check the payment against.
      const baseAmount = Number(draftRef.current?.final_cost ?? pricingInfo.totalCost);
      const paymentAmount = isPartial ? Math.round(baseAmount * 10) / 100 : baseAmount;

      console.log('💰 Payment calculation:', {
        originalCost: selectedQuote.total_cost,
        updatedCost: baseAmount,
        paymentAmount: paymentAmount,
        isPartial: isPartial,
        pricingInfo: pricingInfo
      });

      // First create a payment order
      const orderResponse = await api.post('/payments/create-order', {
        amount: paymentAmount,
        currency: 'INR',
        receipt: `booking_${Date.now()}`,
        notes: {
          booking_type: 'shipping',
          carrier: selectedQuote.carrier_name,
          user_id: user.id,
          payment_type: isPartial ? 'partial' : 'full',
          shipment_id: draftRef.current?.id,
          shipment_number: draftRef.current?.shipment_number,
          total_amount: baseAmount,
          paid_amount: paymentAmount,
          original_quote_amount: selectedQuote.total_cost,
          updated_amount: baseAmount,
          actual_weight: pricingInfo.actualWeight,
          volumetric_weight: pricingInfo.volumetricWeight,
          chargeable_weight: pricingInfo.chargeableWeight
        }
      });

      const orderData = orderResponse.data;
      console.log('Payment order created:', orderData);

      // Set the order data for the Razorpay hook
      setRazorpayOrderData(orderData);

      // Call payment directly with the order data
      await handleDirectPayment(orderData, isPartial);

    } catch (error) {
      console.error('Error creating payment order:', error);
      setLoading(false);
      setPaymentProcessing(false);

      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') setPaymentError(detail);
      toast({
        title: "Payment Error",
        description: typeof detail === 'string' ? detail : "Could not initiate payment. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleDirectPayment = async (orderData, isPartial = false) => {
    console.log('Initiating direct payment with order data:', orderData);

    if (!window.Razorpay) {
      toast({
        title: "Payment Error",
        description: "Razorpay SDK not loaded. Please try again.",
        variant: "destructive"
      });
      setLoading(false);
      return;
    }

    // Use the key ID returned from the backend order (prioritized) or environment variable
    const razorpayKeyId = orderData.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID;

    // A placeholder/mock key makes the gateway show its own generic error page and
    // a raw browser alert. Catch it here and say something useful instead.
    const keyLooksUnusable =
      !razorpayKeyId ||
      /mock|your_key|placeholder|xxx/i.test(razorpayKeyId) ||
      !/^rzp_(test|live)_/.test(razorpayKeyId);

    if (keyLooksUnusable) {
      console.warn('Razorpay key unusable, refusing to open checkout:', razorpayKeyId);
      setLoading(false);
      setPaymentProcessing(false);
      setPaymentError(
        'Online payment is not available right now. Please try another payment method, or contact support if this continues.'
      );
      toast({
        title: "Online payment unavailable",
        description: "We could not start a secure payment session. Please choose another method.",
        variant: "destructive"
      });
      const section = document.querySelector('[data-payment-section]');
      if (section) section.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    const options = {
      key: razorpayKeyId,
      amount: orderData.amount, // Already in paise from backend
      currency: orderData.currency,
      name: 'XFas Logistics',
      description: `Shipping via ${selectedQuote.carrier_name}`,
      order_id: orderData.order_id,
      // Add customer prefill data to reduce risk check failures
      prefill: {
        name: bookingData.senderName || 'Test User',
        email: bookingData.senderEmail || 'test@razorpay.com',
        contact: bookingData.senderPhone || '9999999999'
      },
      theme: {
        color: '#3399cc'
      },
      // image: '/logo192.png', // Removed to avoid CORS issues
      handler: async function (response) {
        // confirm-payment verifies the payment itself - the signature, the
        // amount, and that it has not already paid for something else.
        const method = selectedPaymentMethod === 'wallet' ? 'wallet' : (isPartial ? 'partial' : 'razorpay');
        await processBookingCreation({
          payment_method: method,
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        }, Number(orderData.amount) / 100);
      },
      prefill: {
        name: bookingData.senderName,
        email: bookingData.senderEmail || user.email,
        contact: bookingData.senderPhone
      },
      notes: {
        booking_type: 'shipping',
        customer_id: user.id
      },
      theme: {
        color: '#f97316'
      },
      modal: {
        ondismiss: function () {
          console.log('Payment modal dismissed by user');
          setLoading(false);
          setPaymentProcessing(false);
          setPaymentError('Payment was cancelled before it completed. You can try again, or choose a different payment method below.');
          toast({
            title: "Payment Cancelled",
            description: "Payment was cancelled by user",
            variant: "destructive"
          });
        }
      }
    };

    console.log('Razorpay options:', {
      key: options.key,
      amount: options.amount,
      currency: options.currency,
      order_id: options.order_id,
      name: options.name
    });

    try {
      const razorpay = new window.Razorpay(options);

      razorpay.on('payment.failed', function (response) {
        console.log('Payment failed:', response.error);
        setLoading(false);
        setPaymentProcessing(false);
        setPaymentError(response.error?.description || 'Your payment could not be completed. Please try again, or choose a different payment method below.');

        toast({
          title: "Payment Failed",
          description: response.error.description || "Payment could not be processed",
          variant: "destructive"
        });
      });

      console.log('Opening Razorpay checkout...');
      razorpay.open();
    } catch (error) {
      console.error('Error opening Razorpay:', error);
      setLoading(false);
      setPaymentProcessing(false);
      setPaymentError('We could not open the payment gateway. Please try again, or choose a different payment method below.');

      toast({
        title: "Payment Error",
        description: "Could not open payment gateway. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Built in one place: the draft is created from this before payment, and
  // nothing downstream ever reconstructs it.
  const buildBookingRequest = () => {
    // Pricing as the customer sees it
      const updatedPricingInfo = pricingInfo;

      // Prepare booking request
    const bookingRequest = {
        quote_id: selectedQuote.quote_id || quoteId || pricedQuoteRef.current?.quote_id,
        // Include updated pricing information
        updated_pricing: {
          original_cost: selectedQuote.total_cost,  // Always the raw quote price, before any discount
          updated_cost: updatedPricingInfo.totalCost,
          actual_weight: updatedPricingInfo.actualWeight,
          volumetric_weight: updatedPricingInfo.volumetricWeight,
          chargeable_weight: updatedPricingInfo.chargeableWeight,
          is_price_updated: updatedPricingInfo.isUpdated,
          content_items: bookingData.contentItems.map(item => ({
            description: item.description,
            quantity: parseInt(item.quantity) || 1,
            value: parseFloat(item.value) || 0,
            hsnCode: item.hsnCode || ''
          }))
        },
        sender: {
          name: bookingData.senderName,
          company: bookingData.senderCompany || null,
          phone: bookingData.senderPhone,
          email: bookingData.senderEmail || null,
          street: `${bookingData.senderAddressLine1}${bookingData.senderAddressLine2 ? ', ' + bookingData.senderAddressLine2 : ''}`,
          city: bookingData.senderCity,
          state: bookingData.senderState || null,
          postal_code: bookingData.senderPostalCode,
          country: bookingData.senderCountry,
          landmark: bookingData.senderLandmark || null
        },
        recipient: {
          name: bookingData.recipientName,
          company: bookingData.recipientCompany || null,
          phone: bookingData.recipientPhone,
          email: bookingData.recipientEmail || null,
          street: `${bookingData.recipientAddressLine1}${bookingData.recipientAddressLine2 ? ', ' + bookingData.recipientAddressLine2 : ''}`,
          city: bookingData.recipientCity,
          state: bookingData.recipientState || null,
          postal_code: bookingData.recipientPostalCode,
          country: bookingData.recipientCountry,
          landmark: bookingData.recipientLandmark || null
        },
        package_info: {
          type: selectedQuote.shipment_type || 'parcel',
          dimensions: {
            // Use first package box dimensions, or fallback to quote dimensions
            length: bookingData.packageBoxes[0]?.length || selectedQuote.length || 30,
            width: bookingData.packageBoxes[0]?.width || selectedQuote.width || 20,
            height: bookingData.packageBoxes[0]?.height || selectedQuote.height || 15,
            weight: updatedPricingInfo.actualWeight || selectedQuote.weight || 1
          },
          declared_value: bookingData.contentItems.reduce((total, item) => {
            return total + (parseFloat(item.value) || 0);
          }, 0),
          contents_description: bookingData.contentItems.map(item =>
            `${item.description} (Qty: ${item.quantity})`
          ).join(', '),
          quantity: bookingData.contentItems.reduce((total, item) => {
            return total + (parseInt(item.quantity) || 0);
          }, 0), // Use total quantity from all content items
          fragile: bookingData.fragile,
          dangerous_goods: bookingData.dangerousGoods
        },
        carrier_name: selectedQuote.carrier_name,
        service_type: selectedQuote.service_level || 'standard',
        insurance_required: selectedQuote.insurance_required || false,
        signature_required: selectedQuote.signature_required || false,
        custom_reference: bookingData.hsnCode || null, // Use HSN code as custom reference for now
        notes: bookingData.notes || null,
        payment_method: selectedPaymentMethod,
        // Include final pricing for backend processing
        final_cost: updatedPricingInfo.totalCost, // Always send the full cost
        pickup_method: pickupMethod, // the server adds the pickup charge for 'pickup'
        actual_payment_amount: selectedPaymentMethod === 'partial' ? updatedPricingInfo.totalCost * 0.1 : updatedPricingInfo.totalCost, // Send actual amount paid
        chargeable_weight: updatedPricingInfo.chargeableWeight,
        volumetric_weight: updatedPricingInfo.volumetricWeight
      };
    return bookingRequest;
  };

  // The booking already exists as a draft, created before the customer paid.
  // This only confirms it. Nothing here invents a shipment number, an AWB or a
  // success message: if the confirmation fails, the draft is on the server with
  // every detail, and the customer is told plainly.
  // onlineAmount: what the payment gateway actually charged, when it did.
  const processBookingCreation = async (payment, onlineAmount = null) => {
    const paymentId = payment?.razorpay_payment_id || null;
    // With no online payment, nothing has been charged unless the server
    // confirmed the booking: the wallet is only debited as part of that.
    const paidOnline = Boolean(paymentId);
    const price = Number(draftRef.current?.final_cost ?? pricingInfo.totalCost);
    const amountPaid = onlineAmount != null
      ? onlineAmount
      : (payment?.payment_method === 'partial' ? Math.round(price * 10) / 100 : price);

    if (!termsAccepted) {
      toast({
        title: "Terms and Conditions Required",
        description: "Terms and conditions must be accepted to complete booking.",
        variant: "destructive"
      });
      setLoading(false);
      setPaymentProcessing(false);
      return;
    }
    if (!payment?.payment_method) {
      toast({
        title: "Payment Required",
        description: "Payment must be completed before creating booking.",
        variant: "destructive"
      });
      setLoading(false);
      setPaymentProcessing(false);
      return;
    }

    const draft = draftRef.current;
    if (!draft?.id) {
      // Should be impossible - the draft is created before payment - but if it
      // is missing, say so rather than fabricating a confirmation.
      await bookingsAPI.recordBookingAttempt({
        attempt_id: attemptId, status: 'failed', payment_id: paymentId,
        error: 'No draft booking found at confirmation time',
        payload: buildBookingRequest(),
      });
      setPaymentProcessing(false);
      setLoading(false);
      if (paidOnline) {
        setBookingFailure({ paymentId, reference: attemptId, amount: amountPaid });
      } else {
        setPaymentError('We could not find your booking. Nothing has been charged - please try again.');
      }
      return;
    }

    const showConfirmed = async (confirmed, reference) => {
      await bookingsAPI.recordBookingAttempt({
        attempt_id: attemptId, status: 'completed', payment_id: reference,
        shipment_id: draft.id, shipment_number: confirmed.shipment_number,
      });

      await autoSaveAddresses(draft.sender, draft.recipient);

      setBookingResult({ ...draft, status: confirmed.status || 'booked',
        payment_info: { ...(draft.payment_info || {}), status: 'paid', transaction_id: reference,
          payment_method: payment.payment_method,
          amount: confirmed.amount_paid ?? amountPaid,
          wallet_amount: confirmed.wallet_amount || 0,
          online_amount: confirmed.online_amount || 0 } });
      setCurrentStep(5);
      setPaymentProcessing(false);
      if (payment.payment_method === 'wallet') refreshWalletBalance();

      const walletPart = Number(confirmed.wallet_amount || 0);
      toast({
        title: "Booking Confirmed",
        description: walletPart > 0
          ? `Your booking reference is ${confirmed.shipment_number}. ₹${walletPart.toLocaleString('en-IN')} was paid from your wallet.`
          : `Your booking reference is ${confirmed.shipment_number}`,
      });

      if (onComplete) onComplete(draft);
    };

    try {
      const confirmed = await bookingsAPI.confirmBookingPayment(draft.id, payment);
      await showConfirmed(confirmed, confirmed.transaction_id || paymentId);
    } catch (error) {
      console.error('Could not confirm the booking after payment:', error);
      const detail = error?.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (error?.message || String(error));

      if (!paidOnline) {
        // A lost response can hide a success, so ask the server which it was.
        let current = null;
        try {
          current = await bookingsAPI.getBooking(draft.id);
        } catch (lookupError) {
          console.error('Could not re-check the booking:', lookupError);
        }
        if (current && current.status && current.status !== 'draft') {
          await showConfirmed({ shipment_number: current.shipment_number, status: current.status,
            amount_paid: current.payment_info?.amount }, current.payment_info?.transaction_id);
          return;
        }
        await bookingsAPI.recordBookingAttempt({
          attempt_id: attemptId, status: 'declined', payment_method: payment.payment_method,
          shipment_id: draft.id, shipment_number: draft.shipment_number, error: message,
        });
        setPaymentProcessing(false);
        refreshWalletBalance();
        setPaymentError(`${message} Nothing has been charged.`);
        toast({
          title: "Payment not completed",
          description: `${message} Nothing has been charged.`,
          variant: "destructive",
        });
        return;
      }

      await bookingsAPI.recordBookingAttempt({
        attempt_id: attemptId, status: 'failed', payment_id: paymentId,
        shipment_id: draft.id, shipment_number: draft.shipment_number, error: message,
      });
      setPaymentProcessing(false);
      setBookingFailure({
        paymentId,
        reference: draft.shipment_number || attemptId,
        amount: amountPaid,
      });
      toast({
        title: "Payment received - confirming your booking",
        description: "Your payment went through and your booking is saved. Our team will confirm it shortly.",
        variant: "destructive",
        duration: 12000,
      });
    } finally {
      setLoading(false);
    }
  };

  // The server prices a booking only from a quote it stored. A rate picked on
  // the quote explorer never was one, so store a quote for this parcel now.
  // If its price is not the one on screen, show the new price instead of
  // charging it.
  const ensureStoredQuote = async () => {
    if (selectedQuote.quote_id || quoteId) return;
    if (pricedQuoteRef.current) return;
    const { quoteRequest } = buildRequoteRequest();
    const response = await quotesAPI.createQuote(quoteRequest);
    const match = (response?.carrier_quotes || []).find(
      (q) => q.carrier_name === selectedQuote.carrier_name
    );
    if (!match) {
      const err = new Error(`${selectedQuote.carrier_name} is not available for this parcel. Please choose another carrier.`);
      err.priceChanged = true;
      throw err;
    }
    const stored = {
      ...selectedQuote,
      ...match,
      to_country: selectedQuote.to_country,
      shipment_type: selectedQuote.shipment_type,
      quote_id: response.id,
    };
    if (Math.abs(Number(match.total_cost) - Number(selectedQuote.total_cost)) >= 0.01) {
      if (onQuoteUpdate) onQuoteUpdate(stored);
      const err = new Error(`The price for this parcel is now ₹${Math.round(match.total_cost).toLocaleString('en-IN')}. Please check it and press Pay again.`);
      err.priceChanged = true;
      throw err;
    }
    pricedQuoteRef.current = stored;
  };

  // Creates the shipment before payment. Called once; the draft is reused if
  // the customer retries payment, so a retry cannot produce two bookings.
  const ensureDraftBooking = async () => {
    await ensureStoredQuote();
    const bookingRequest = buildBookingRequest();
    // The draft is what gets booked, so reuse it only if nothing in it has
    // changed since (an address edited after a cancelled payment, say) and it
    // is still within the day its price holds. The payment method is not part
    // of the draft.
    const draftKey = JSON.stringify({ ...bookingRequest, payment_method: null, actual_payment_amount: null });
    const existing = draftRef.current;
    const createdAt = existing?.created_at ? Date.parse(`${String(existing.created_at).replace(/Z$/, '')}Z`) : NaN;
    const fresh = Number.isFinite(createdAt) && Date.now() - createdAt < 23 * 60 * 60 * 1000;
    if (existing?.id && existing._key === draftKey && fresh) return existing;
    await bookingsAPI.recordBookingAttempt({
      attempt_id: attemptId, status: 'pending',
      payment_method: selectedPaymentMethod, amount: pricingInfo.totalCost,
      payload: bookingRequest,
    });
    const draft = await bookingsAPI.createDraftBooking(bookingRequest);
    draft._key = draftKey;
    draftRef.current = draft;
    await bookingsAPI.recordBookingAttempt({
      attempt_id: attemptId, status: 'draft_created',
      shipment_id: draft?.id, shipment_number: draft?.shipment_number,
    });
    return draft;
  };

  // Print document functions
  const handlePrintDocument = async (documentType) => {
    if (!bookingResult) {
      toast({
        title: "Error",
        description: "No booking data available for printing",
        variant: "destructive"
      });
      return;
    }

    console.log(`📄 Attempting to download ${documentType} for booking:`, {
      id: bookingResult.id,
      shipment_number: bookingResult.shipment_number,
      status: bookingResult.status
    });

    try {
      let downloadUrl;
      let filename;

      switch (documentType) {
        case 'shipping-label':
          toast({
            title: "Generating Shipping Label",
            description: "Please wait while we generate your shipping label...",
          });
          downloadUrl = await bookingsAPI.downloadShippingLabel(bookingResult.id);
          filename = `shipping_label_${bookingResult.shipment_number}.pdf`;
          break;

        case 'shipping-invoice':
          toast({
            title: "Generating Shipping Invoice",
            description: "Please wait while we generate your commercial invoice...",
          });
          downloadUrl = await bookingsAPI.downloadShippingInvoice(bookingResult.id);
          filename = `shipping_invoice_${bookingResult.shipment_number}.pdf`;
          break;

        case 'payment-receipt':
          toast({
            title: "Generating Payment Receipt",
            description: "Please wait while we generate your payment receipt...",
          });
          downloadUrl = await bookingsAPI.downloadPaymentReceipt(bookingResult.id);
          filename = `payment_receipt_${bookingResult.shipment_number}.pdf`;
          break;

        default:
          throw new Error('Unknown document type');
      }

      console.log(`✅ Successfully generated ${documentType}, creating download link...`);

      // Create download link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(downloadUrl);

      console.log(`✅ Download triggered for ${filename}`);

      toast({
        title: "Download Complete",
        description: `${filename} has been downloaded successfully.`,
      });

    } catch (error) {
      console.error(`❌ Error downloading ${documentType}:`, {
        error: error.message,
        status: error.response?.status,
        data: error.response?.data,
        bookingId: bookingResult.id
      });

      // Check if it's a server error (document generation failed)
      if (error.response?.status === 404) {
        toast({
          title: "Document Not Available",
          description: "The document is not yet available. It will be sent to your email shortly.",
        });
      } else if (error.response?.status === 500) {
        // Server error - try email fallback
        toast({
          title: "Document Generation",
          description: "Having trouble generating the document. It will be sent to your email shortly.",
        });

        // Show additional helpful message
        setTimeout(() => {
          toast({
            title: "Email Confirmation",
            description: `All booking documents will be sent to your registered email address within 15 minutes.`,
            variant: "default"
          });
        }, 3000);
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        toast({
          title: "Authentication Error",
          description: "Please log in again to download documents.",
          variant: "destructive"
        });
      } else {
        // Other errors - show specific error message
        toast({
          title: "Download Failed",
          description: error.response?.data?.detail || error.message || "Failed to generate document. Please try again.",
          variant: "destructive"
        });
      }
    }
  };



  // Platform country codes arrive like UNITED_KINGDOM
  const prettyCountry = (v) =>
    (v || '')
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  // One-line recap shown on a collapsed (already completed) step.
  const stepSummary = (stepId) => {
    switch (stepId) {
      case 1: {
        const bits = [
          bookingData.senderName,
          bookingData.senderPhone,
          bookingData.senderEmail,
          bookingData.senderCity,
        ].filter(Boolean);
        return bits.length ? bits.join(' · ') : null;
      }
      case 10: {
        // Only once the customer has been through it - until then pickup is
        // just the default, not their choice.
        if (orderIndex(currentStep) <= orderIndex(10)) return null;
        return pickupMethod === 'drop_off'
          ? 'Drop-off at our office · Free'
          : `Door pickup · ${inr(pricingInfo.pickupCharge)}`;
      }
      case 2: {
        const bits = [
          bookingData.recipientName,
          bookingData.recipientPhone,
          bookingData.recipientEmail,
          bookingData.recipientCity,
          prettyCountry(bookingData.recipientCountry),
        ].filter(Boolean);
        return bits.length ? bits.join(' · ') : null;
      }
      case 3: {
        const boxes = bookingData.packageBoxes || [];
        const chargeable = boxes.reduce((sum, b) => sum + boxWeights(b).chargeable, 0);
        if (!chargeable) return null;
        const boxLabel = `${boxes.length} ${boxes.length === 1 ? 'box' : 'boxes'}`;
        return `${boxLabel} · ${chargeable.toFixed(1)} kg chargeable`;
      }
      case 6: {
        const total = (bookingData.contentItems || []).reduce(
          (t, it) => t + (parseFloat(it.value) || 0) * (parseInt(it.quantity) || 0),
          0
        );
        const named = (bookingData.contentItems || []).filter((it) => it.description);
        const label = named.length
          ? `${named.length} ${named.length === 1 ? 'item' : 'items'}`
          : null;
        if (!label && !total) return null;
        return [label, total ? `₹${total.toLocaleString('en-IN')}` : null]
          .filter(Boolean)
          .join(' · ');
      }
      case 7: {
        // Only the flags belong in the title; free-text notes are too long
        const flags = [
          bookingData.fragile ? 'Fragile' : null,
          bookingData.dangerousGoods ? 'Dangerous goods' : null,
        ].filter(Boolean);
        return flags.length ? flags.join(' · ') : null;
      }
      case 8:
        return ratesValid ? 'Rates updated' : 'Action needed';
      case 9:
        return selectedPaymentMethod
          ? `Paying by ${selectedPaymentMethod === 'razorpay' ? 'card / UPI / netbanking' : selectedPaymentMethod}`
          : null;
      default:
        return null;
    }
  };

  // Until the customer has filled in a box we have no weight for their parcel -
  // the quote only carries a starting-rate reference weight. Saying "0.0 kg"
  // states something about their shipment that is not true.
  const weightOrDash = (kg) => (kg > 0 ? `${kg.toFixed(1)} kg` : '—');

  // Quote Selection Summary Component (compact design like user's image)
  const QuoteSelectionSummary = () => {
    const stats = [
      { label: 'Destination', value: prettyCountry(bookingData.recipientCountry), flag: true },
      { label: 'Weight', value: weightOrDash(enteredWeights.chargeable) },
      {
        label: 'Type',
        value: selectedQuote?.shipment_type
          ? selectedQuote.shipment_type.charAt(0).toUpperCase() + selectedQuote.shipment_type.slice(1)
          : 'Parcel',
      },
      { label: 'Volumetric', value: weightOrDash(enteredWeights.volumetric) },
    ];

    return (
      <div className="flex w-full flex-col gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:gap-6">
        <div className="flex items-center gap-3 min-w-0 sm:w-[200px] sm:shrink-0">
          <CarrierLogo
            src={getCarrierLogo(selectedQuote?.carrier_name)}
            name={selectedQuote?.carrier_name}
            className="h-8 w-[92px] shrink-0"
          />
          <span className="min-w-0">
            <span className="block text-[14px] font-bold leading-tight text-slate-900">
              {carrierDisplayName(selectedQuote?.carrier_name)}
            </span>
            <span className="block text-[11px] capitalize text-slate-500">
              {selectedQuote?.service_level || 'standard'} service
            </span>
          </span>
        </div>

        <div className="grid flex-1 grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-[minmax(0,1fr)_4.5rem_4.5rem_5rem]">
          {stats.map((st) => (
            <div key={st.label} className="min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {st.label}
              </p>
              <p className="mt-0.5 flex items-center gap-1.5 text-[13px] font-semibold text-slate-800">
                {st.flag ? <FlagIcon country={st.value} className="h-4 shrink-0" /> : null}
                {st.value}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Shown instead of a confirmation when payment succeeded but the booking did
  // not. It states what happened rather than inventing a shipment number.
  const renderBookingFailure = () => (
    <div className="mx-auto max-w-xl rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
      <h3 className="text-xl font-bold text-amber-900">Your payment went through</h3>
      <p className="mt-2 text-sm text-amber-900">
        We could not finish creating the booking automatically. Nothing is lost - your
        payment and full shipment details are with us, and our team will confirm your
        booking shortly.
      </p>
      <div className="mt-4 rounded-lg bg-white p-4 text-left text-sm">
        <p className="flex justify-between gap-4">
          <span className="text-slate-500">Amount paid</span>
          <span className="font-semibold text-slate-900">
            &#8377;{Math.round(bookingFailure?.amount || 0).toLocaleString('en-IN')}
          </span>
        </p>
        {bookingFailure?.paymentId && (
          <p className="mt-2 flex justify-between gap-4">
            <span className="text-slate-500">Payment reference</span>
            <span className="font-mono text-[12px] font-semibold text-slate-900">{bookingFailure.paymentId}</span>
          </p>
        )}
        <p className="mt-2 flex justify-between gap-4">
          <span className="text-slate-500">Booking reference</span>
          <span className="font-mono text-[12px] font-semibold text-slate-900">{bookingFailure?.reference}</span>
        </p>
      </div>
      <p className="mt-4 text-xs text-amber-900">
        Please keep these references. If you need us sooner, contact support and quote them.
      </p>
    </div>
  );

  const renderStepContent = () => {
    // Debug log
    console.log('Rendering step', currentStep, 'with', savedAddresses.length, 'addresses');

    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold">Sender Information</h3>
              <div className="flex items-center space-x-2">
                <BookOpen className="w-4 h-4 text-orange-500" />
                {savedAddresses.length > 0 ? (
                  <Select onValueChange={(value) => loadSavedAddress(value, 'sender')}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Load saved address" />
                    </SelectTrigger>
                    <SelectContent>
                      {savedAddresses.map((addr) => (
                        <SelectItem key={addr.id} value={addr.id}>
                          {addr.label || `${addr.name} - ${addr.city}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  user ? (
                    <span className="text-sm text-slate-500">No saved addresses</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowLoginModal(true)}
                      className="text-sm font-semibold text-orange-600 hover:text-orange-700 hover:underline"
                    >
                      Already a member? Log in to use saved addresses
                    </button>
                  )
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={bookingData.senderName}
                  onChange={(e) => handleInputChange('senderName', e.target.value)}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={bookingData.senderPhone}
                  onChange={(e) => handleInputChange('senderPhone', e.target.value)}
                  placeholder="+91-9876543210"
                />
              </div>
              <div>
                <Label>Email Address (Optional)</Label>
                <Input
                  type="email"
                  value={bookingData.senderEmail}
                  onChange={(e) => handleInputChange('senderEmail', e.target.value)}
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <Label>Company (Optional)</Label>
                <Input
                  value={bookingData.senderCompany}
                  onChange={(e) => handleInputChange('senderCompany', e.target.value)}
                  placeholder="Company Name"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Address Line 1 *</Label>
                <Input
                  value={bookingData.senderAddressLine1}
                  onChange={(e) => handleInputChange('senderAddressLine1', e.target.value)}
                  placeholder="123 Main Street"
                />
              </div>

              <div>
                <Label>Address Line 2 (Optional)</Label>
                <Input
                  value={bookingData.senderAddressLine2}
                  onChange={(e) => handleInputChange('senderAddressLine2', e.target.value)}
                  placeholder="Apartment, suite, unit, building, floor, etc."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>City/Town *</Label>
                <Input
                  value={bookingData.senderCity}
                  onChange={(e) => handleInputChange('senderCity', e.target.value)}
                  placeholder="Mumbai"
                />
              </div>
              <div>
                <Label>State/Province</Label>
                <Input
                  value={bookingData.senderState}
                  onChange={(e) => handleInputChange('senderState', e.target.value)}
                  placeholder="Maharashtra"
                />
              </div>
              <div>
                <Label>Postcode/Zipcode *</Label>
                <Input
                  type="text"
                  placeholder="Enter postcode/zipcode"
                  value={bookingData.senderPostalCode}
                  onChange={(e) => handleInputChange('senderPostalCode', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Country (Frozen)</Label>
                <Select value={bookingData.senderCountry} disabled>
                  <SelectTrigger className="bg-gray-100 cursor-not-allowed">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="India">🇮🇳 India</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Country is set based on your quote origin</p>
              </div>
            </div>

            <div>
              <Label>Landmark (Optional)</Label>
              <Input
                value={bookingData.senderLandmark}
                onChange={(e) => handleInputChange('senderLandmark', e.target.value)}
                placeholder="Near Railway Station"
              />
            </div>
          </div>
        );

      case 10: {
        // Pickup is priced per kg by the sender's PIN code. Its price stays on
        // show whichever option is chosen; choosing only moves the selection.
        const pickupAddress = [
          bookingData.senderAddressLine1,
          bookingData.senderCity,
          bookingData.senderPostalCode,
        ].filter(Boolean).join(', ');
        return (
          <div className="space-y-4" data-test="pickup-choice">
            <h3 className="text-lg font-semibold">How should we collect your parcel?</h3>
            <div className="grid gap-3 md:grid-cols-2">
              <label className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 ${pickupMethod === 'pickup' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                <input
                  type="radio"
                  name="pickupMethod"
                  data-test="pickup-option"
                  className="mt-1"
                  checked={pickupMethod === 'pickup'}
                  onChange={() => setPickupMethod('pickup')}
                />
                <span className="flex-1">
                  <span className="flex items-center justify-between gap-2 font-medium text-gray-900">
                    <span className="flex items-center gap-1.5"><Truck className="h-4 w-4 text-orange-500" /> Pickup from your address</span>
                    <span data-test="pickup-price" className="whitespace-nowrap text-sm">{pickupPriceLabel}</span>
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500">{pickupNote}</span>
                  {pickupAddress ? (
                    <span className="mt-1 block text-xs text-gray-700" data-test="pickup-address">{pickupAddress}</span>
                  ) : null}
                </span>
              </label>
              <label className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 ${pickupMethod === 'drop_off' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'}`}>
                <input
                  type="radio"
                  name="pickupMethod"
                  data-test="dropoff-option"
                  className="mt-1"
                  checked={pickupMethod === 'drop_off'}
                  onChange={() => setPickupMethod('drop_off')}
                />
                <span className="flex-1">
                  <span className="flex items-center justify-between gap-2 font-medium text-gray-900">
                    <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-orange-500" /> Drop it at our office</span>
                    <span className="text-sm text-green-700">Free</span>
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500">
                    Madhuban Tower, A-200 Road no 4, Gali no 10, Mahipalpur, New Delhi 110037
                  </span>
                </span>
              </label>
            </div>
            {pickupMethod === 'pickup' && pickupQuote.status === 'idle' && (
              <p className="text-sm text-red-600" role="alert">
                Pickup needs the 6-digit PIN code of your address.{' '}
                <button type="button" className="font-medium underline" onClick={() => setCurrentStep(1)}>
                  Fix it in Sender Details
                </button>{' '}
                or choose to drop the parcel at our office.
              </p>
            )}
            {pickupMethod === 'pickup' && pickupQuote.status === 'unavailable' && (
              <p className="text-sm text-red-600" role="alert" data-test="pickup-unavailable">
                Pickup isn't available at PIN code {pickupQuote.pincode} yet. Please choose to drop the parcel at our office.
              </p>
            )}
            {pickupMethod === 'pickup' && pickupQuote.status === 'error' && (
              <p className="text-sm text-red-600" role="alert">
                We couldn't check pickup just now.{' '}
                <button type="button" className="font-medium underline" onClick={() => setPickupRetry((n) => n + 1)}>
                  Try again
                </button>{' '}
                or choose to drop the parcel at our office.
              </p>
            )}
          </div>
        );
      }

      case 2:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold">Recipient Information</h3>
              <div className="flex items-center space-x-2">
                <BookOpen className="w-4 h-4 text-orange-500" />
                {savedAddresses.length > 0 ? (
                  <Select onValueChange={(value) => loadSavedAddress(value, 'recipient')}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Load saved address" />
                    </SelectTrigger>
                    <SelectContent>
                      {savedAddresses.map((addr) => (
                        <SelectItem key={addr.id} value={addr.id}>
                          {addr.label || `${addr.name} - ${addr.city}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  user ? (
                    <span className="text-sm text-slate-500">No saved addresses</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowLoginModal(true)}
                      className="text-sm font-semibold text-orange-600 hover:text-orange-700 hover:underline"
                    >
                      Already a member? Log in to use saved addresses
                    </button>
                  )
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input
                  value={bookingData.recipientName}
                  onChange={(e) => handleInputChange('recipientName', e.target.value)}
                  placeholder="Jane Smith"
                />
              </div>
              <div>
                <Label>Phone Number *</Label>
                <Input
                  value={bookingData.recipientPhone}
                  onChange={(e) => handleInputChange('recipientPhone', e.target.value)}
                  placeholder="+91-9876543211"
                />
              </div>
              <div>
                <Label>Email Address (Optional)</Label>
                <Input
                  type="email"
                  value={bookingData.recipientEmail}
                  onChange={(e) => handleInputChange('recipientEmail', e.target.value)}
                  placeholder="jane@example.com"
                />
              </div>
              <div>
                <Label>Company (Optional)</Label>
                <Input
                  value={bookingData.recipientCompany}
                  onChange={(e) => handleInputChange('recipientCompany', e.target.value)}
                  placeholder="Company Name"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Address Line 1 *</Label>
                <Input
                  value={bookingData.recipientAddressLine1}
                  onChange={(e) => handleInputChange('recipientAddressLine1', e.target.value)}
                  placeholder="456 Park Avenue"
                />
              </div>

              <div>
                <Label>Address Line 2 (Optional)</Label>
                <Input
                  value={bookingData.recipientAddressLine2}
                  onChange={(e) => handleInputChange('recipientAddressLine2', e.target.value)}
                  placeholder="Apartment, suite, unit, building, floor, etc."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Label>City/Town *</Label>
                <Input
                  value={bookingData.recipientCity}
                  onChange={(e) => handleInputChange('recipientCity', e.target.value)}
                  placeholder="Delhi"
                />
              </div>
              <div>
                <Label>State/Province</Label>
                <Input
                  value={bookingData.recipientState}
                  onChange={(e) => handleInputChange('recipientState', e.target.value)}
                  placeholder="State or province"
                />
              </div>
              <div>
                <Label>Postcode/Zipcode *</Label>
                <Input
                  type="text"
                  placeholder="Enter postcode/zipcode"
                  value={bookingData.recipientPostalCode}
                  onChange={(e) => handleInputChange('recipientPostalCode', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Country (Frozen)</Label>
                <Select value={bookingData.recipientCountry} disabled>
                  <SelectTrigger className="bg-gray-100 cursor-not-allowed">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={bookingData.recipientCountry}>
                      {bookingData.recipientCountry}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Country is set based on your quote destination</p>
              </div>
            </div>

            <div>
              <Label>Landmark (Optional)</Label>
              <Input
                value={bookingData.recipientLandmark}
                onChange={(e) => handleInputChange('recipientLandmark', e.target.value)}
                placeholder="Near Metro Station"
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            {/* Package Boxes with Weight and Dimensions */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold">Package Boxes</h4>
                  <p className="text-sm text-slate-600">Weight and dimensions for each physical box/package</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addPackageBox}
                  className="flex items-center space-x-1"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Package Box</span>
                </Button>
              </div>

              {/* One row per box: inputs and the derived weights share a single line */}
              <div className="space-y-2">
                <div className="hidden gap-3 px-3 lg:grid lg:grid-cols-[2rem_repeat(4,minmax(0,1fr))_5rem_5rem_2rem]">
                  {['', 'Weight (kg)', 'Length (cm)', 'Width (cm)', 'Height (cm)', 'Volumetric', 'Chargeable', ''].map((h, i) => (
                    <span key={i} className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      {h}
                    </span>
                  ))}
                </div>

                {bookingData.packageBoxes.map((box, index) => {
                  const { volumetric, chargeable } = boxWeights(box);
                  const vol = roundChargeable(volumetric);
                  return (
                    <div
                      key={box.id}
                      className="grid grid-cols-2 items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 sm:grid-cols-4 lg:grid-cols-[2rem_repeat(4,minmax(0,1fr))_5rem_5rem_2rem] lg:gap-3"
                    >
                      <span className="col-span-2 text-[12px] font-bold text-slate-500 sm:col-span-4 lg:col-span-1">
                        #{index + 1}
                      </span>

                      <div className="lg:contents">
                        <Input
                          type="number"
                          inputMode="decimal"
                          step="0.1"
                          min="0"
                          placeholder="Weight"
                          aria-label={`Box ${index + 1} weight in kg`}
                          className="h-9"
                          value={box.weight}
                          onChange={(e) => updatePackageBox(box.id, 'weight', e.target.value)}
                          required
                        />
                      </div>
                      <Input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        step="1"
                        placeholder="Length"
                        aria-label={`Box ${index + 1} length in cm`}
                        className="h-9"
                        value={box.length}
                        onChange={(e) => updatePackageBox(box.id, 'length', e.target.value)}
                        required
                      />
                      <Input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        step="1"
                        placeholder="Width"
                        aria-label={`Box ${index + 1} width in cm`}
                        className="h-9"
                        value={box.width}
                        onChange={(e) => updatePackageBox(box.id, 'width', e.target.value)}
                        required
                      />
                      <Input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        step="1"
                        placeholder="Height"
                        aria-label={`Box ${index + 1} height in cm`}
                        className="h-9"
                        value={box.height}
                        onChange={(e) => updatePackageBox(box.id, 'height', e.target.value)}
                        required
                      />

                      <span className="text-[13px] font-semibold text-slate-700">
                        <span className="lg:hidden text-[10px] uppercase tracking-wider text-slate-400">Volumetric </span>
                        {vol} kg
                      </span>
                      <span className="text-[13px] font-bold text-orange-600">
                        <span className="lg:hidden text-[10px] uppercase tracking-wider text-slate-400">Chargeable </span>
                        {chargeable} kg
                      </span>

                      <span className="flex justify-end">
                        {bookingData.packageBoxes.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removePackageBox(box.id)}
                            aria-label={`Remove box ${index + 1}`}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>



            {/* Package Summary */}
          </div>
        );

      case 6:
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                What&apos;s inside your packages, for customs &amp; insurance
              </p>
              <Button type="button" variant="outline" size="sm" onClick={addContentItem}>
                <Plus className="h-4 w-4 mr-1" />
                Add Item
              </Button>
            </div>

            {/* One row per item: description, qty, value, HSN and line total */}
            <div className="space-y-2">
              <div className="hidden gap-3 px-3 lg:grid lg:grid-cols-[2rem_minmax(0,2.2fr)_4.5rem_6rem_7rem_5.5rem_2rem]">
                {['', 'Description', 'Qty', 'Value / item', 'HSN (optional)', 'Line total', ''].map((h, i) => (
                  <span key={i} className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    {h}
                  </span>
                ))}
              </div>

              {bookingData.contentItems.map((item, index) => {
                const lineTotal =
                  (parseFloat(item.value) || 0) * (parseInt(item.quantity) || 0);
                return (
                  <div
                    key={item.id}
                    className="grid grid-cols-2 items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 lg:grid-cols-[2rem_minmax(0,2.2fr)_4.5rem_6rem_7rem_5.5rem_2rem]"
                  >
                    <span className="col-span-2 text-[12px] font-bold text-slate-500 lg:col-span-1">
                      #{index + 1}
                    </span>

                    <Input
                      className="col-span-2 h-9 lg:col-span-1"
                      placeholder="e.g. Cotton T-shirts"
                      aria-label={`Item ${index + 1} description`}
                      value={item.description}
                      onChange={(e) => updateContentItem(item.id, 'description', e.target.value)}
                      required
                    />
                    <Input
                      type="number"
                      inputMode="numeric"
                      min="1"
                      step="1"
                      className="h-9"
                      placeholder="Qty"
                      aria-label={`Item ${index + 1} quantity`}
                      value={item.quantity}
                      onChange={(e) => updateContentItem(item.id, 'quantity', e.target.value)}
                      required
                    />
                    <Input
                      type="number"
                      inputMode="numeric"
                      min="0"
                      step="100"
                      className="h-9"
                      placeholder="Value"
                      aria-label={`Item ${index + 1} value per item in rupees`}
                      value={item.value}
                      onChange={(e) => updateContentItem(item.id, 'value', e.target.value)}
                      required
                    />
                    <Input
                      className="h-9"
                      placeholder="HSN"
                      aria-label={`Item ${index + 1} HSN code`}
                      value={item.hsnCode}
                      onChange={(e) => updateContentItem(item.id, 'hsnCode', e.target.value)}
                    />

                    <span className="text-[13px] font-bold tabular-nums text-slate-800">
                      <span className="lg:hidden text-[10px] uppercase tracking-wider text-slate-400">Total </span>
                      &#8377;{lineTotal.toLocaleString('en-IN')}
                    </span>

                    <span className="flex justify-end">
                      {bookingData.contentItems.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeContentItem(item.id)}
                          aria-label={`Remove item ${index + 1}`}
                          className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-slate-500">
              HSN codes help customs clear international shipments faster.
            </p>


            {/* Total declared value for customs */}
            <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <span className="text-sm font-semibold text-slate-700">Total declared value</span>
              <span className="text-[17px] font-extrabold tabular-nums text-slate-900">
                &#8377;{bookingData.contentItems
                  .reduce((total, item) => total + (parseFloat(item.value) || 0) * (parseInt(item.quantity) || 1), 0)
                  .toLocaleString('en-IN')}
              </span>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-4">
            <div>
              <Label>Special Instructions (Optional)</Label>
              <Textarea
                value={bookingData.notes}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                placeholder="Handle with care, fragile item, delivery instructions..."
                rows={3}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="fragile"
                  checked={bookingData.fragile}
                  onCheckedChange={(checked) => handleInputChange('fragile', checked)}
                />
                <Label htmlFor="fragile">Fragile item - handle with care</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="dangerous"
                  checked={bookingData.dangerousGoods}
                  onCheckedChange={(checked) => handleInputChange('dangerousGoods', checked)}
                />
                <Label htmlFor="dangerous">Contains dangerous goods</Label>
              </div>
            </div>

            {/* Prohibited Items Section */}
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-orange-900">Important: Prohibited Items</h4>
                  <p className="text-sm text-orange-700">Please ensure your shipment does not contain any prohibited items</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowProhibitedItems(!showProhibitedItems)}
                  className="flex items-center space-x-1 text-orange-600 border-orange-200"
                >
                  <AlertTriangle className="h-4 w-4" />
                  <span>{showProhibitedItems ? 'Hide' : 'View'} Prohibited Items</span>
                </Button>
              </div>

              {showProhibitedItems && (
                <Card className="bg-orange-50 border-orange-200" e="border-orange-200 bg-orange-50">
                  <CardContent className="pt-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <AlertTriangle className="h-5 w-5 text-orange-600" />
                        <h4 className="font-semibold text-orange-900">Items That Cannot Be Shipped</h4>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowProhibitedItems(false)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-orange-900">
                      {prohibitedItems.map((item, idx) => (
                        <div key={idx} className="flex items-start space-x-2">
                          <span className="text-orange-600 mt-1">•</span>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 p-3 bg-orange-100 rounded-lg">
                      <p className="text-sm text-orange-800 font-medium">
                        ⚠️ Shipping prohibited items may result in package confiscation, legal action, and additional charges.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        );

      case 8: {
        // One entry per carrier - the current quote and the alternatives can
        // both contain the same carrier, which is what allowed two rows to look
        // selected at once.
        const byCarrier = new Map();
        [...freshQuotes, ...(selectedQuote ? [selectedQuote] : []), ...alternativeQuotes].forEach((q) => {
          if (q?.carrier_name && !byCarrier.has(q.carrier_name)) byCarrier.set(q.carrier_name, q);
        });
        const carriers = [...byCarrier.values()].sort((a, b) => a.total_cost - b.total_cost);
        const highlights = computeQuoteHighlights(carriers);

        return (
          <div className="space-y-4">
            <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-amber-900">
                  Your parcel size changed the chargeable weight
                </p>
                <p className="mt-0.5 text-sm text-amber-800">
                  The price shown was for {pricingInfo.chargeableWeight?.toFixed(1) || '0.0'} kg.
                  Fetch fresh rates and confirm a carrier before continuing.
                </p>
              </div>
              <Button
                type="button"
                onClick={handleManualQuoteRefresh}
                disabled={refreshInProgress}
                className="shrink-0 bg-[#F17221] hover:bg-[#d9661d] text-white"
              >
                {refreshInProgress ? 'Updating...' : 'Get fresh rates'}
              </Button>
            </div>

            {carriers.length > 0 && ratesValid && (
              <div className="space-y-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Choose a carrier at the new weight
                </p>
                {carriers.map((q, i) => {
                  const isPicked = q.carrier_name === selectedQuote?.carrier_name;
                  return (
                    <button
                      key={`${q.carrier_name}-${i}`}
                      type="button"
                      onClick={() => onQuoteUpdate && onQuoteUpdate({ ...selectedQuote, ...q })}
                      className={`flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                        isPicked
                          ? 'border-[#F17221] bg-orange-50/50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <CarrierLogo
                        src={getCarrierLogo(q.carrier_name)}
                        name={q.carrier_name}
                        className="h-7 w-[74px] shrink-0"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="text-[14px] font-bold text-slate-900">
                            {carrierDisplayName(q.carrier_name)}
                          </span>
                          {badgesForIndex(i, highlights).map((b) => (
                            <span
                              key={b.label}
                              className={`${b.cls} rounded px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wider text-white`}
                            >
                              {b.label === 'Fastest delivery' ? 'Fastest' : b.label}
                            </span>
                          ))}
                        </span>
                        {tatToText(q.tat) && (
                          <span className="block text-[12px] text-slate-500">
                            {tatToText(q.tat)}
                          </span>
                        )}
                      </span>
                      <span className="shrink-0 text-right">
                        <span className="block text-[17px] font-extrabold tabular-nums text-slate-900">
                          &#8377;{Math.round(q.total_cost || 0).toLocaleString('en-IN')}
                        </span>
                        {isPicked && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[#F17221]">
                            Selected
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      }

      case 4:
        return (
          <div className="space-y-4">
            {weightMismatch && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="text-sm font-bold text-amber-900">
                  This price no longer matches your parcel
                </p>
                <p className="mt-1 text-sm text-amber-800">
                  The quote was priced for {quotedChargeable.toFixed(1)} kg chargeable, but your
                  boxes now come to {enteredWeights.chargeable.toFixed(1)} kg. Get fresh rates so you
                  are charged the right amount.
                </p>
                <Button
                  type="button"
                  onClick={() => {
                    setRequotePending(true);
                    setCurrentStep(8);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="mt-3 bg-[#F17221] hover:bg-[#d9661d]"
                >
                  Get fresh rates
                </Button>
              </div>
            )}
            {/* Three-up review: what is being shipped, from whom, to whom */}
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              {/* Shipment */}
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Shipment
                </p>
                <div className="mt-3 flex items-center gap-3">
                  <CarrierLogo
                    src={getCarrierLogo(selectedQuote?.carrier_name)}
                    name={selectedQuote?.carrier_name}
                    className="h-8 w-[84px] shrink-0"
                  />
                  <span className="min-w-0">
                    <span className="block text-[14px] font-bold text-slate-900">
                      {selectedQuote?.carrier_name}
                    </span>
                    <span className="block text-[11px] capitalize text-slate-500">
                      {selectedQuote?.service_level || 'standard'} service
                    </span>
                  </span>
                </div>

                <p className="mt-3 text-[26px] font-extrabold leading-none tabular-nums tracking-tight text-[#F17221]">
                  &#8377;{Math.round(pricingInfo.totalCost || 0).toLocaleString('en-IN')}
                </p>
                <p className="mt-1 text-[11px] text-slate-400" data-test="price-breakdown">
                  {pickupMethod === 'drop_off'
                    ? 'Shipping · drop-off at our office (free)'
                    : pickupQuote.status === 'ok'
                      ? `Shipping ${inr(pricingInfo.shippingCost)} + pickup ${inr(pricingInfo.pickupCharge)}`
                      : 'Shipping · pickup charge added once your PIN code is in'}
                </p>

                <div className="mt-3 space-y-1.5 border-t border-slate-100 pt-3 text-[12px]">
                  <p className="flex items-center justify-between gap-2">
                    <span className="text-slate-500">Destination</span>
                    <span className="flex items-center gap-1.5 font-semibold text-slate-800">
                      <FlagIcon country={prettyCountry(bookingData.recipientCountry)} className="h-4" />
                      {prettyCountry(bookingData.recipientCountry)}
                    </span>
                  </p>
                  <p className="flex items-center justify-between gap-2">
                    <span className="text-slate-500">Chargeable weight</span>
                    <span className="font-semibold text-slate-800">
                      {(enteredWeights.chargeable || quotedChargeable).toFixed(1)} kg
                    </span>
                  </p>
                  <p className="flex items-center justify-between gap-2">
                    <span className="text-slate-500">Declared value</span>
                    <span className="font-semibold text-slate-800">
                      &#8377;{bookingData.contentItems
                        .reduce((t, it) => t + (parseFloat(it.value) || 0) * (parseInt(it.quantity) || 0), 0)
                        .toLocaleString('en-IN')}
                    </span>
                  </p>
                </div>
              </div>

              {/* Sender */}
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    <User className="h-3.5 w-3.5" aria-hidden="true" />
                    From
                  </p>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="text-[11px] font-semibold text-orange-600 hover:underline"
                  >
                    Edit
                  </button>
                </div>
                <p className="mt-3 text-[14px] font-bold text-slate-900">{bookingData.senderName}</p>
                <p className="mt-1 text-[12px] leading-relaxed text-slate-600">
                  {[bookingData.senderAddressLine1, bookingData.senderAddressLine2].filter(Boolean).join(', ')}
                  <br />
                  {[bookingData.senderCity, bookingData.senderPostalCode].filter(Boolean).join(', ')}
                  <br />
                  {bookingData.senderCountry || 'India'}
                </p>
                <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-[12px] text-slate-600">
                  {bookingData.senderPhone ? <p>{bookingData.senderPhone}</p> : null}
                  {bookingData.senderEmail ? <p className="truncate">{bookingData.senderEmail}</p> : null}
                </div>
                <p className="mt-3 flex items-center justify-between gap-2 border-t border-slate-100 pt-3 text-[12px]" data-test="review-collection">
                  <span className="font-semibold text-slate-800">
                    {pickupMethod === 'drop_off'
                      ? 'You drop it at our office (free)'
                      : `Door pickup · ${inr(pricingInfo.pickupCharge)}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(10)}
                    className="text-[11px] font-semibold text-orange-600 hover:underline"
                  >
                    Change
                  </button>
                </p>
              </div>

              {/* Recipient */}
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                    To
                  </p>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    className="text-[11px] font-semibold text-orange-600 hover:underline"
                  >
                    Edit
                  </button>
                </div>
                <p className="mt-3 text-[14px] font-bold text-slate-900">{bookingData.recipientName}</p>
                <p className="mt-1 text-[12px] leading-relaxed text-slate-600">
                  {[bookingData.recipientAddressLine1, bookingData.recipientAddressLine2].filter(Boolean).join(', ')}
                  <br />
                  {[bookingData.recipientCity, bookingData.recipientPostalCode].filter(Boolean).join(', ')}
                  <br />
                  {prettyCountry(bookingData.recipientCountry)}
                </p>
                <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-[12px] text-slate-600">
                  {bookingData.recipientPhone ? <p>{bookingData.recipientPhone}</p> : null}
                  {bookingData.recipientEmail ? <p className="truncate">{bookingData.recipientEmail}</p> : null}
                </div>
              </div>
            </div>
          </div>
        );

      case 9:
        return (
          <div className="space-y-4">
            {/* Payment Method Selection */}
            <div data-payment-section className="space-y-4">
              {paymentError && (
                <div className="rounded-lg border border-red-300 bg-red-50 p-4">
                  <div className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-red-800">Payment not completed</p>
                      <p className="text-sm text-red-700 mt-0.5">{paymentError}</p>
                      <p className="text-xs text-red-600 mt-1.5">You can try again, or choose a different payment method below.</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPaymentError(null)}
                      className="text-red-400 hover:text-red-600"
                      aria-label="Dismiss"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              {/* Mock Payment Notice */}
              {process.env.REACT_APP_USE_MOCK_PAYMENTS === 'true' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-sm font-medium text-blue-800">Development Mode</span>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">
                    Mock payments enabled - No real money will be charged
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {/* Razorpay Option */}
                <div
                  className={`border rounded-lg p-3 cursor-pointer transition-all ${selectedPaymentMethod === 'razorpay'
                    ? 'border-orange-500 bg-orange-50'
                    : !selectedPaymentMethod
                      ? 'border-red-200 hover:border-red-300'
                      : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => setSelectedPaymentMethod('razorpay')}
                >
                  <div className="flex items-start space-x-3">
                    <input
                      type="radio"
                      name="payment"
                      value="razorpay"
                      checked={selectedPaymentMethod === 'razorpay'}
                      onChange={() => setSelectedPaymentMethod('razorpay')}
                      className="mt-1 shrink-0 text-orange-500"
                    />
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <span className="flex items-center gap-2">
                          <CreditCard className="w-5 h-5 text-blue-600" />
                          <span className="font-semibold">Pay Online</span>
                        </span>
                        <span className="flex flex-wrap items-center gap-1.5">
                          {['VISA', 'mastercard', 'AMEX', 'RuPay', 'UPI'].map((m) => (
                            <span
                              key={m}
                              className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[9px] font-extrabold tracking-tight text-slate-600"
                            >
                              {m}
                            </span>
                          ))}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Cards, Net Banking, UPI and Wallets
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Note: UPI availability depends on amount limits (₹1L daily limit)
                      </p>
                    </div>
                  </div>
                </div>

                {/* Wallet Payment Option */}
                {walletUsable && (
                  <div
                    className={`border rounded-lg p-3 cursor-pointer transition-all ${
                      selectedPaymentMethod === 'wallet'
                        ? 'border-orange-500 bg-orange-50'
                        : !selectedPaymentMethod
                        ? 'border-red-200 hover:border-red-300'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedPaymentMethod('wallet')}
                  >
                    <div className="flex items-start space-x-3">
                      <input
                        type="radio"
                        name="payment"
                        value="wallet"
                        checked={selectedPaymentMethod === 'wallet'}
                        onChange={() => setSelectedPaymentMethod('wallet')}
                        className="mt-1 shrink-0 text-orange-500"
                      />
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <Wallet className="w-5 h-5 shrink-0 text-purple-600" />
                          <span className="font-semibold">Pay from Wallet</span>
                          <Badge className="shrink-0 whitespace-nowrap bg-purple-100 text-purple-800 text-xs">
                            Balance: ₹{Math.round(walletBalance.balance).toLocaleString('en-IN')}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {walletBalance.balance >= pricingInfo.totalCost
                            ? 'Pay full amount from wallet'
                            : `Pay ₹${Math.round(walletBalance.balance).toLocaleString('en-IN')} from wallet + ₹${Math.round(pricingInfo.totalCost - walletBalance.balance).toLocaleString('en-IN')} online`
                          }
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                            Instant
                          </span>
                          {walletBalance.balance < pricingInfo.totalCost && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              Split Payment
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Partial Payment Option - NO KYC REQUIRED */}
                <div
                  className={`border rounded-lg p-3 cursor-pointer transition-all ${selectedPaymentMethod === 'partial'
                    ? 'border-orange-500 bg-orange-50'
                    : !selectedPaymentMethod
                      ? 'border-red-200 hover:border-red-300'
                      : 'border-gray-200 hover:border-gray-300'
                    }`}
                  onClick={() => setSelectedPaymentMethod('partial')}
                >
                  <div className="flex items-start space-x-3">
                    <input
                      type="radio"
                      name="payment"
                      value="partial"
                      checked={selectedPaymentMethod === 'partial'}
                      onChange={() => setSelectedPaymentMethod('partial')}
                      className="mt-1 shrink-0 text-orange-500"
                    />
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="flex items-center gap-2">
                          <Receipt className="w-5 h-5 shrink-0 text-blue-600" />
                          <span className="font-semibold">Pay at Pickup</span>
                        </span>
                        <Badge className="shrink-0 whitespace-nowrap bg-green-100 text-green-800 text-xs">No KYC Required</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Pay 10% now, remaining at pickup
                      </p>
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">10% minimum required</span>
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Pay ₹{Math.round(pricingInfo.totalCost * 0.1).toLocaleString('en-IN')} now</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        All payment methods available including UPI, Cards, Net Banking
                      </p>
                    </div>
                  </div>
                </div>

                {/* Payment Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Amount to Pay:</span>
                    <span className="text-xl font-bold text-orange-600">
                      ₹{(() => {
                        const baseAmount = pricingInfo.totalCost;

                        if (selectedPaymentMethod === 'wallet') {
                          const walletAmt = walletBalance?.balance || 0;
                          return walletAmt >= baseAmount ? '0' : Math.round(baseAmount - walletAmt).toLocaleString('en-IN');
                        } else if (selectedPaymentMethod === 'partial') {
                          return Math.round(baseAmount * 0.1).toLocaleString('en-IN'); // 10% for partial payment
                        } else {
                          return Math.round(baseAmount).toLocaleString('en-IN'); // Full amount
                        }
                      })()}
                    </span>
                  </div>
                  
                  {selectedPaymentMethod === 'wallet' && (() => {
                    const walletAmt = walletBalance?.balance || 0;
                    const totalCost = pricingInfo.totalCost;
                    const razorpayAmt = Math.max(0, totalCost - walletAmt);
                    return (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs text-gray-600">
                          From Wallet: ₹{Math.round(Math.min(walletAmt, totalCost)).toLocaleString('en-IN')}
                        </p>
                        {razorpayAmt > 0 && (
                          <p className="text-xs text-gray-600">
                            Online: ₹{Math.round(razorpayAmt).toLocaleString('en-IN')}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                  
                  {selectedPaymentMethod === 'partial' && (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-gray-600">
                        Paying now: ₹{Math.round(pricingInfo.totalCost * 0.1).toLocaleString('en-IN')} (10%)
                      </p>
                      <p className="text-xs text-gray-600">
                        Remaining: ₹{Math.round(pricingInfo.totalCost * 0.9).toLocaleString('en-IN')} (at pickup)
                      </p>
                    </div>
                  )}
                  {selectedPaymentMethod === 'razorpay' && (
                    <p className="text-xs text-gray-600 mt-1">You will be taken to a secure payment page</p>
                  )}
                  {!selectedPaymentMethod && (
                    <p className="text-xs text-red-600 mt-1">Please select a payment method above</p>
                  )}
                </div>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="rounded-lg border border-slate-200 p-4">
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="terms"
                    className="mt-1"
                    checked={termsAccepted}
                    onCheckedChange={(checked) => {
                      setTermsAccepted(checked);
                      // Clear error when user accepts terms
                      if (checked) {
                        setShowTermsError(false);
                      }
                    }}
                  />
                  <Label htmlFor="terms" className="text-sm leading-relaxed">
                    I agree to the <a href="/terms-and-conditions/" target="_blank" className="text-orange-600 hover:underline">Terms and Conditions</a> and
                    <a href="/privacy-policy/" target="_blank" className="text-orange-600 hover:underline ml-1">Privacy Policy</a>.
                    I confirm that the shipment does not contain any prohibited items and all information provided is accurate.
                  </Label>
                </div>
                {showTermsError && !termsAccepted && (
                  <div className="mt-2 text-sm text-red-600 flex items-center space-x-1 animate-pulse">
                    <AlertTriangle className="w-4 h-4" />
                    <span>Please accept the terms and conditions to proceed with payment.</span>
                  </div>
                )}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>

              <div className="mt-4">
                <h3 className="text-2xl font-bold text-green-800 mb-2">Booking Confirmed!</h3>
                <p className="text-gray-600">Your shipment has been booked successfully.</p>
              </div>
            </div>

            {bookingResult && (
              <>
                {/* Booking Details */}
                <Card>
                  <CardHeader>
                    <CardTitle>Booking Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span>Booking ID:</span>
                      <span className="font-mono">{bookingResult.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Shipment Number:</span>
                      <span className="font-mono">{bookingResult.shipment_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>AWB/Tracking Number:</span>
                      <span className="font-mono font-bold text-orange-600">
                        {bookingResult.carrier_info?.tracking_number}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Carrier:</span>
                      <span>{bookingResult.carrier_info?.carrier_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Status:</span>
                      <Badge className="bg-green-100 text-green-800">{bookingResult.status}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>Payment Method:</span>
                      <span className="capitalize">
                        {(() => {
                          // How this booking was paid. The selection itself can be
                          // cleared afterwards (a wallet emptied by the payment).
                          const paidWith = bookingResult.payment_info?.payment_method || selectedPaymentMethod;
                          if (paidWith === 'razorpay') return 'Online Payment';
                          if (paidWith === 'wallet') {
                            return bookingResult.payment_info?.online_amount > 0 ? 'Wallet + Online Payment' : 'Wallet';
                          }
                          return 'Partial Payment';
                        })()}
                      </span>
                    </div>
                    {bookingResult.carrier_info?.tracking_number && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                          <span className="text-sm text-blue-800">
                            Booking confirmation and tracking details will be sent to your email shortly.
                          </span>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Print Options */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Printer className="w-5 h-5" />
                      <span>Print Documents</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Shipping Label */}
                      <Button
                        variant="outline"
                        className="flex flex-col items-center space-y-2 h-auto py-4"
                        onClick={() => handlePrintDocument('shipping-label')}
                      >
                        <Package className="w-8 h-8 text-blue-600" />
                        <div className="text-center">
                          <div className="font-semibold">Shipping Label</div>
                          <div className="text-xs text-gray-500">Print & attach to package</div>
                        </div>
                      </Button>

                      {/* Shipping Invoice */}
                      <Button
                        variant="outline"
                        className="flex flex-col items-center space-y-2 h-auto py-4"
                        onClick={() => handlePrintDocument('shipping-invoice')}
                      >
                        <FileText className="w-8 h-8 text-green-600" />
                        <div className="text-center">
                          <div className="font-semibold">Shipping Invoice</div>
                          <div className="text-xs text-gray-500">Commercial invoice</div>
                        </div>
                      </Button>

                      {/* Payment Receipt */}
                      <Button
                        variant="outline"
                        className="flex flex-col items-center space-y-2 h-auto py-4"
                        onClick={() => handlePrintDocument('payment-receipt')}
                      >
                        <Receipt className="w-8 h-8 text-purple-600" />
                        <div className="text-center">
                          <div className="font-semibold">Payment Receipt</div>
                          <div className="text-xs text-gray-500">Payment confirmation</div>
                        </div>
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Next Steps */}
                <Card>
                  <CardHeader>
                    <CardTitle>Next Steps</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-orange-600">1</span>
                        </div>
                        <div>
                          <p className="font-medium">Print and attach shipping label</p>
                          <p className="text-sm text-gray-600">Print the shipping label and securely attach it to your package</p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-orange-600">2</span>
                        </div>
                        <div>
                          <p className="font-medium">Package your items securely</p>
                          <p className="text-sm text-gray-600">Ensure items are properly packed and protected</p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-orange-600">3</span>
                        </div>
                        <div>
                          {pickupMethod === 'drop_off' ? (
                            <>
                              <p className="font-medium">Drop your parcel at our office</p>
                              <p className="text-sm text-gray-600">Madhuban Tower, A-200 Road no 4, Gali no 10, Mahipalpur, New Delhi 110037</p>
                            </>
                          ) : (
                            <>
                              <p className="font-medium">We collect your parcel</p>
                              <p className="text-sm text-gray-600">Our team will contact you to arrange the pickup</p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Contact Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold">📧</span>
                </div>
                <div>
                  <p className="font-medium text-blue-900">Confirmation sent to your email</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Booking confirmation and shipping documents have been sent to your email address.
                    You will receive SMS updates on your shipment progress.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => window.location.href = '/dashboard'}
              >
                <Package className="w-4 h-4 mr-2" />
                View My Shipments
              </Button>
              <Button
                className="flex-1 bg-orange-500 hover:bg-orange-600"
                onClick={() => window.location.href = '/quote'}
              >
                Create New Booking
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Payment succeeded but the booking did not: show that, and nothing else.
  // Leaving the steps on screen invites a second payment attempt.
  if (bookingFailure) {
    return (
      <div className="max-w-4xl mx-auto space-y-6 py-6">
        {renderBookingFailure()}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Button variant="outline" onClick={onBack} className="flex items-center space-x-2">
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Quotes</span>
        </Button>
        <h2 className="text-2xl font-bold">Create Booking</h2>
      </div>

      {/* Quote first, then the steps. Numbers are display-only: the Quote card
          is not a step, so each step renders as its id + 1. */}
      <div className="space-y-3">
        {currentStep !== 5 && (
          <Card className={`overflow-hidden ${quoteOpen ? 'border-orange-300' : 'border-green-200 bg-green-50/40'}`}>
            <button
              type="button"
              onClick={() => setQuoteOpen((o) => !o)}
              className="w-full flex items-center gap-3 px-4 sm:px-6 py-3.5 text-left hover:bg-slate-50"
            >
              <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white">
                <CheckCircle className="w-4 h-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900">Quote</span>
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                    Selected
                  </span>
                </span>
              </span>
              <span className="mr-3 flex shrink-0 items-center gap-2.5">
                <CarrierLogo
                  src={getCarrierLogo(selectedQuote?.carrier_name)}
                  name={selectedQuote?.carrier_name}
                  className="h-6 w-[64px] shrink-0"
                />
                <span className="text-[17px] font-extrabold leading-none tabular-nums tracking-tight text-[#F17221]">
                  &#8377;{Math.round(selectedQuote?.total_cost || 0).toLocaleString('en-IN')}
                </span>
              </span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-300 ${quoteOpen ? 'rotate-180' : ''}`}
              />
            </button>
            <div
              className={`grid transition-all duration-300 ease-in-out ${
                quoteOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
              }`}
            >
              <div className="overflow-hidden">
                <CardContent className="px-4 sm:px-6 pb-2 pt-0">
                  <QuoteSelectionSummary />
                </CardContent>
              </div>
            </div>
          </Card>
        )}

        {stepByOrder.map((step) => {
          const isCurrent = currentStep === step.id;
          const isDone = orderIndex(currentStep) > orderIndex(step.id);
          const isLocked = orderIndex(currentStep) < orderIndex(step.id);
          if (step.id === 5 && currentStep !== 5) return null;
          if (step.id !== 5 && currentStep === 5) return null;
          // Boxes only apply to parcels
          if (step.id === 3 && selectedQuote?.shipment_type === 'document') return null;

          return (
            <Card
              key={step.id}
              className={`overflow-hidden transition-colors ${
                isCurrent
                  ? 'border-orange-300 shadow-sm'
                  : isDone
                    ? 'border-green-200 bg-green-50/40'
                    : 'border-slate-200'
              } ${isLocked ? 'opacity-60' : ''}`}
            >
              <button
                type="button"
                onClick={() => {
                  if (isDone) {
                    setCurrentStep(step.id);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                disabled={!isDone}
                className={`w-full flex items-center gap-3 px-4 sm:px-6 py-3.5 text-left ${
                  isDone ? 'cursor-pointer hover:bg-slate-50' : 'cursor-default'
                }`}
              >
                <span
                  className={`flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold transition-colors ${
                    isDone
                      ? 'bg-green-600 text-white'
                      : isCurrent
                        ? 'bg-[#F17221] text-white'
                        : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {isDone ? <CheckCircle className="w-4 h-4" /> : orderIndex(step.id) + 2}
                </span>

                <span className="min-w-0 flex-1">
                  <span className={`block font-semibold ${isCurrent ? 'text-slate-900' : 'text-slate-600'}`}>
                    {step.title}
                  </span>
                </span>

                {stepSummary(step.id) ? (
                  <span className="mr-3 hidden max-w-[55%] shrink-0 truncate text-sm font-medium text-slate-600 sm:block">
                    {stepSummary(step.id)}
                  </span>
                ) : null}

                {isDone ? (
                  <span className="flex-shrink-0 text-xs font-semibold text-orange-600">Edit</span>
                ) : (
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-300 ${isCurrent ? 'rotate-180' : ''}`}
                  />
                )}
              </button>

              <div
                className={`grid transition-all duration-300 ease-in-out ${
                  isCurrent ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}
              >
                <div className="overflow-hidden">
                  <CardContent className="px-4 sm:px-6 pb-6 pt-0 border-t border-slate-100">
                    <div className="pt-5">{isCurrent ? renderStepContent() : null}</div>

                    {/* Step actions live at the end of the open accordion */}
                    {isCurrent && step.id !== 5 && (
                      <div className="mt-6 border-t border-slate-100 pt-4">
                      {/* Say why the pay button is unavailable rather than just greying it out */}
                      {step.id === 9 && (!selectedPaymentMethod || !termsAccepted) && (
                        <p className="mb-3 flex items-center gap-2 text-sm text-amber-700">
                          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                          {!selectedPaymentMethod && !termsAccepted
                            ? 'Choose a payment method and accept the terms to continue'
                            : !selectedPaymentMethod
                              ? 'Choose a payment method to continue'
                              : 'Accept the terms and conditions to continue'}
                        </p>
                      )}
                      <div className="flex items-center justify-between gap-3">
                        <Button
                          variant="outline"
                          onClick={handlePrevious}
                          disabled={orderIndex(currentStep) === 0}
                        >
                          Previous
                        </Button>
                        {step.id === 9 ? (
                          <Button
                            onClick={createBooking}
                            disabled={loading || paymentProcessing || !selectedPaymentMethod || !termsAccepted}
                            className="bg-[#F17221] hover:bg-[#d9661d] disabled:bg-gray-400 disabled:cursor-not-allowed"
                          >
                            {loading || paymentProcessing ? 'Processing Payment...' : 'Confirm Booking & Pay'}
                          </Button>
                        ) : (
                          <Button onClick={handleNext} className="bg-[#F17221] hover:bg-[#d9661d]">
                            {step.id === 4 ? 'Proceed to Payment' : 'Next'}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                        )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Reassurance footer */}
      {currentStep !== 5 && (
        <div className="mt-4 flex flex-col gap-3 rounded-xl border border-blue-100 bg-blue-50/50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2 text-[13px] text-slate-600">
            <ShieldCheck className="h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
            Your data is secure and only used for shipping your parcel.
          </span>
          <span className="flex flex-wrap items-center gap-1.5">
            {['VISA', 'mastercard', 'AMEX', 'RuPay', 'UPI'].map((m) => (
              <span
                key={m}
                className="rounded border border-slate-200 bg-white px-2 py-1 text-[10px] font-extrabold tracking-tight text-slate-600"
              >
                {m}
              </span>
            ))}
          </span>
        </div>
      )}

      {/* Volumetric Weight Info Modal */}
      <VolumetricWeightInfo
        isOpen={showVolumetricInfo}
        onClose={() => setShowVolumetricInfo(false)}
      />

      {/* Sign-in / create account: only reached at the payment step */}
      <LoginModal
        isOpen={showLoginModal}
        redirectOnSuccess={false}
        onClose={() => setShowLoginModal(false)}
        onSwitchToRegister={() => {
          setShowLoginModal(false);
          setShowRegisterModal(true);
        }}
      />
      <RegisterModal
        isOpen={showRegisterModal}
        redirectOnSuccess={false}
        onClose={() => setShowRegisterModal(false)}
        onSwitchToLogin={() => {
          setShowRegisterModal(false);
          setShowLoginModal(true);
        }}
      />
    </div>
  );
};

// VolumetricWeightInfo Component
const VolumetricWeightInfo = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Understanding Volumetric Weight</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Introduction */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-900 mb-2">What is Volumetric Weight?</h3>
            <p className="text-blue-800">
              Volumetric weight (also called dimensional weight) is a pricing technique used by shipping companies
              to account for packages that are large but light. It ensures fair pricing based on the space a package
              occupies in the delivery vehicle.
            </p>
          </div>

          {/* Formula */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">How is it Calculated?</h3>
            <div className="bg-white border border-gray-300 rounded-lg p-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900 mb-2">
                  Volumetric Weight = (Length × Width × Height) ÷ 5000
                </div>
                <div className="text-sm text-gray-600">
                  Dimensions in centimeters, result in kilograms
                </div>
              </div>
            </div>
            <p className="text-gray-700">
              The divisor (5000) is the standard volumetric factor used by most international carriers.
              Some domestic carriers may use different factors.
            </p>
          </div>

          {/* Examples */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Examples</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Example 1 */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">📦 Small Heavy Package</h4>
                <div className="space-y-2 text-sm">
                  <div><strong>Dimensions:</strong> 20 × 15 × 10 cm</div>
                  <div><strong>Actual Weight:</strong> 5 kg</div>
                  <div><strong>Volumetric Weight:</strong> (20×15×10) ÷ 5000 = 0.6 kg</div>
                  <div className="bg-green-100 p-2 rounded">
                    <strong>Chargeable Weight:</strong> 5 kg (actual weight applies)
                  </div>
                </div>
              </div>

              {/* Example 2 */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-orange-900 mb-2">📦 Large Light Package</h4>
                <div className="space-y-2 text-sm">
                  <div><strong>Dimensions:</strong> 50 × 40 × 30 cm</div>
                  <div><strong>Actual Weight:</strong> 2 kg</div>
                  <div><strong>Volumetric Weight:</strong> (50×40×30) ÷ 5000 = 12 kg</div>
                  <div className="bg-orange-100 p-2 rounded">
                    <strong>Chargeable Weight:</strong> 12 kg (volumetric weight applies)
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Why it matters */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-yellow-900 mb-2">Why Does This Matter?</h3>
            <ul className="space-y-2 text-yellow-800">
              <li className="flex items-start space-x-2">
                <span className="text-yellow-600 mt-1">•</span>
                <span>Shipping companies charge based on the <strong>higher</strong> of actual weight or volumetric weight</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-yellow-600 mt-1">•</span>
                <span>Large, light packages (like pillows, clothing) often cost more due to volumetric weight</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-yellow-600 mt-1">•</span>
                <span>Compact, heavy packages (like books, electronics) are usually charged by actual weight</span>
              </li>
            </ul>
          </div>

          {/* Tips */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-purple-900 mb-2">💡 Tips to Reduce Shipping Costs</h3>
            <ul className="space-y-2 text-purple-800">
              <li className="flex items-start space-x-2">
                <span className="text-purple-600 mt-1">•</span>
                <span>Use the smallest possible box that safely fits your items</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-purple-600 mt-1">•</span>
                <span>Remove excess packaging and air-filled spaces</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-purple-600 mt-1">•</span>
                <span>Consider splitting large, light shipments into multiple smaller boxes</span>
              </li>
              <li className="flex items-start space-x-2">
                <span className="text-purple-600 mt-1">•</span>
                <span>Use our calculator to compare different box sizes before shipping</span>
              </li>
            </ul>
          </div>

          {/* Interactive Calculator */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">📏 Quick Calculator</h3>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div>
                <Label className="text-sm">Length (cm)</Label>
                <Input type="number" placeholder="50" className="h-8" />
              </div>
              <div>
                <Label className="text-sm">Width (cm)</Label>
                <Input type="number" placeholder="40" className="h-8" />
              </div>
              <div>
                <Label className="text-sm">Height (cm)</Label>
                <Input type="number" placeholder="30" className="h-8" />
              </div>
            </div>
            <div className="text-center">
              <Button variant="outline" size="sm">Calculate Volumetric Weight</Button>
            </div>
          </div>
        </div>
      </div>

      {/* KYC Required Modal */}
      {showKycModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">KYC Verification Required</h3>
              <button
                onClick={() => setShowKycModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Complete KYC to Use Partial Payment</p>
                  <p className="text-sm text-gray-600">This feature requires verified KYC</p>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700">
                  Partial payment option allows you to pay only 10% upfront and the remaining amount at pickup. 
                  To use this feature, you need to complete your KYC verification.
                </p>
              </div>

              <div className="space-y-2 text-sm text-gray-600">
                <p className="font-medium text-gray-900">Benefits after KYC:</p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Access to partial payment option</li>
                  <li>Pay at Pickup payments</li>
                  <li>Wallet usage for transactions</li>
                  <li>Higher transaction limits</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowKycModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setShowKycModal(false);
                  window.location.href = '/profile/kyc';
                }}
                className="flex-1 bg-orange-500 hover:bg-orange-600"
              >
                Complete KYC
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default BookingFlow;